/* ------------------------------------------------------------------------------
@name: Order
--------------------------------------------------------------------------------- */

// --- variables
import { API_URL, BASE_URL } from "variables";

// --- utilities
import {
  DataTable,
  FormatDate,
  Form,
  HttpRequest,
  SweetAlert,
} from "utilities";

// Form Validation
const ElementSelector = [
  { id: "order_id" },
  { id: "customer", validation: { required: true } },
  { id: "reseller", validation: { required: true } },
  { id: "phone", validation: { required: true } },
  { id: "batch_number", validation: { required: true } },
  { id: "depature_date", validation: { required: true } },
  {
    id: "quantity",
    selector: "[data-field='quantity']",
    validation: { required: true, numeric: true, min: 1 },
  },
];

const ElementEvents = ["input", "blur"];

let cameraStream = null;
// capturedPhotos stores objects: { src: dataURL-or-url, id: optional-server-id, isServer: bool }
let capturedPhotos = [];
let photosToRemove = []; // server photo ids queued for deletion
const MAX_PHOTOS = 5;
let editPhotoIndex = null;
const isMobileDevice = () => {
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
};

const Order = (() => {
  // -----------------------
  // Helpers
  // -----------------------
  const dataURLtoBlob = (dataurl) => {
    const arr = dataurl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/jpeg";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const parseNumber = (val) => {
    if (val === null || val === undefined) return 0;
    const s = String(val).trim().replace(/\s+/g, "");
    if (s === "") return 0;
    const normalized = s.replace(/,/g, ".");
    const n = parseFloat(normalized);
    return isNaN(n) ? 0 : n;
  };

  const formatNumber = (num, decimals = 2) =>
    Number(num || 0).toFixed(decimals);

  // -----------------------
  // DataTable
  // -----------------------

  const ORDER_TABLE_STATE_KEY = "thaiStoreOrderTableState";

  const handleRunDataTable = () => {
    // =========================
    // Restore saved state
    // =========================

    let savedState = null;

    try {
      savedState = JSON.parse(
        sessionStorage.getItem(ORDER_TABLE_STATE_KEY) || "null"
      );
    } catch (error) {
      savedState = null;
    }

    if (savedState?.filters) {
      const filters = savedState.filters;

      if ($("#dateRange").length) {
        $("#dateRange").val(filters.dateRange || "");
      }

      if ($("#status").length) {
        $("#status")
          .val(filters.status || "")
          .trigger("change.select2");
      }

      if ($("#statusMobile").length) {
        $("#statusMobile")
          .val(filters.statusMobile || "")
          .trigger("change.select2");
      }

      if ($("#type").length) {
        $("#type")
          .val(filters.type || "")
          .trigger("change.select2");
      }

      if ($("#customer_id").length) {
        $("#customer_id")
          .val(filters.customer_id || "")
          .trigger("change.select2");
      }

      if ($("#reseller_id").length) {
        $("#reseller_id")
          .val(filters.reseller_id || "")
          .trigger("change.select2");
      }

      if ($("#search").length) {
        $("#search").val(filters.search || "");
      }
    }

    // =========================
    // DataTable Setting
    // =========================

    const dataSetting = {
      selector: "js-data-order",

      url: API_URL.orderGet,

      method: "POST",

      responsive: {
        breakpoints: [
          {
            name: "desktop",
            width: Infinity,
          },
          {
            name: "tablet",
            width: 992,
          },
          {
            name: "mobile",
            width: 768,
          },
        ],

        details: {
          type: "column",
          target: 0,
        },
      },

      data: (d) => {
        const _status = $("#status").val();

        const _statusMobile = $("#statusMobile").val();

        const _type = $("#type").val();

        const _date = $("#dateRange").val();

        const _customer_id = $("#customer_id").val();

        const _reseller_id = $("#reseller_id").val();

        const _search = $("#search").val();

        let extraData = {};

        if (_status && _status.length > 0) {
          extraData.status = _status;
        }

        if (_statusMobile && _statusMobile.length > 0) {
          extraData.status = _statusMobile;
        }

        if (_date && _date.length > 0) {
          const range = FormatDate.range(_date);

          extraData.startDate = range.startDate;

          extraData.endDate = range.endDate;
        }

        if (_customer_id && _customer_id.length > 0) {
          extraData.customer_id = _customer_id;
        }

        if (_reseller_id && _reseller_id.length > 0) {
          extraData.reseller_id = _reseller_id;
        }

        if (_type && _type.length > 0) {
          extraData.type = _type;
        }

        // Kalau custom DataTable kamu memakai parameter search bawaan,
        // bagian ini sebenarnya tidak wajib.
        //
        // Tetapi kalau backend memakai parameter search manual,
        // bisa digunakan.
        //
        // if (_search && _search.length > 0) {
        //   extraData.search = _search;
        // }

        return $.extend({}, d, extraData);
      },
    };

    // =========================
    // Filter Setting
    // =========================

    const filterSetting = [
      {
        id: "dateRange",
        event: "change",
      },
      {
        id: "status",
        event: "change",
      },
      {
        id: "statusMobile",
        event: "change",
      },
      {
        id: "search",
        event: "keyup",
      },
      {
        id: "type",
        event: "change",
      },
    ];

    // =========================
    // Normalize Status
    // =========================

    const normalizeStatus = (status) => {
      let raw = String(status ?? "")
        .trim()
        .toUpperCase();

      if (
        raw === "CANCELED" ||
        raw === "CANCELLED" ||
        raw === "CANCEL OUTBOUND" ||
        raw === "CANCEL_OUTBOUND" ||
        raw === "CANCEL OUTBOND" ||
        raw === "CANCEL_OUTBOND"
      ) {
        return "CANCELLED";
      }

      if (raw === "LABEL") {
        return "LABELED";
      }

      return raw;
    };

    // =========================
    // Timeline
    // =========================

    const buildTimelineHtml = (status, rowData = {}) => {
      const steps = [
        {
          key: "INBOUND",
          icon: "ph-package",
          title: "Inbound",
        },
        {
          key: "LABELED",
          icon: "ph-printer",
          title: "Labeled",
        },
        {
          key: "OUTBOUND",
          icon: "ph-car",
          title: "Outbound",
        },
        {
          key: "COMPLETED",
          icon: "ph-check",
          title: "Completed",
        },
        {
          key: "CANCELLED",
          icon: "ph-tray-arrow-down",
          title: "Cancelled",
        },
      ];

      const raw = normalizeStatus(status);

      const isCancelled = raw === "CANCELLED";

      const isOutbound = raw === "OUTBOUND";

      // Cancel button ONLY appears for OUTBOUND status

      const canCancel = isOutbound && !!rowData.id;

      let activeIndex = steps.findIndex((s) => s.key === raw);

      if (activeIndex === -1 && /^\d+$/.test(raw)) {
        const num = Math.max(1, Math.min(parseInt(raw, 10), 4));

        activeIndex = num - 1;
      }

      // If status not found, default to INBOUND

      if (activeIndex === -1) {
        activeIndex = 0;
      }

      // Kalau cancelled, timeline normal berhenti di OUTBOUND

      if (isCancelled && activeIndex > 2) {
        activeIndex = 2;
      }

      const wrapperClass = `timeline-steps lite${
        isCancelled ? " is-cancelled" : ""
      }`;

      let html = `<div class="${wrapperClass}">`;

      steps.forEach((s, i) => {
        let cls = "";

        let attrs = "";

        // Handle cancelled step

        if (isCancelled && s.key === "CANCELLED") {
          cls = "cancelled active";
        } else {
          cls = i <= activeIndex ? "active" : "";
        }

        // Add last-active

        if (cls.includes("active")) {
          if ((isCancelled && i === 2) || (!isCancelled && i === activeIndex)) {
            cls += " last-active";
          }
        }

        // Cancel functionality only available
        // when current order status is OUTBOUND

        if (s.key === "CANCELLED" && canCancel) {
          cls += " is-clickable js-cancel-data";

          attrs += `
          data-id="${rowData.id}"
          data-toggle="tooltip"
          data-placement="top"
          title="Cancel Order"
        `;
        } else {
          attrs += ` title="${s.title}"`;
        }

        html += `
        <div
          class="timeline-steps-item ${cls.trim()}"
          ${attrs}
        >
          <span class="icon">
            <i class="ph ${s.icon}"></i>
          </span>
        </div>
      `;
      });

      html += `</div>`;

      return html;
    };

    // =========================
    // Column Setting
    // =========================

    const columnSetting = [
      {
        targets: 0,

        className: "dt-control",

        orderable: false,

        data: null,

        defaultContent: "",

        responsivePriority: 1,

        visible: false,

        width: "300px",
      },

      {
        targets: 1,

        orderable: false,

        responsivePriority: 1,

        render: (data) => {
          return `
          <div class="custom-control custom-checkbox">

            <input
              type="checkbox"
              class="custom-control-input js-select-data"
              id="check-${data.number}"
              value="${data.id}"
            />

            <label
              class="custom-control-label"
              for="check-${data.number}"
            ></label>

          </div>
        `;
        },
      },

      {
        targets: 2,

        responsivePriority: 2,

        createdCell: function (td) {
          $(td).addClass("order-code");
        },
      },

      {
        targets: 3,

        responsivePriority: 99,
      },

      {
        targets: [4, 5, 7, 8],

        responsivePriority: 99,
      },

      {
        targets: 6,

        render: (status, type, row) => {
          const rowData = row[9] || {};

          return buildTimelineHtml(status, rowData);
        },

        responsivePriority: 99,
      },

      {
        targets: 9,

        responsivePriority: 3,

        className: "text-center",

        render: (data, type, row) => {
          const timelineStatus = data.status || row[6] || "";

          const timelineHtml = buildTimelineHtml(timelineStatus, data);

          const buttonHtml = `
          <button
            type="button"
            data-toggle="tooltip"
            data-placement="left"
            title="Print"
            class="
              btn
              btn-sm
              btn-default
              btn-trans
              waves-effect
              js-print-data
            "
            data-id="${data.id}"
          >
            <i class="ph ph-printer"></i>
          </button>


          <button
            type="button"
            data-toggle="tooltip"
            data-placement="left"
            title="Detail"
            class="
              btn
              btn-sm
              btn-default
              btn-trans
              waves-effect
              js-detail-data
            "
            data-id="${data.id}"
          >
            <i class="ph ph-magnifying-glass-plus"></i>
          </button>


          <a
            href="${API_URL.order}/edit/${data.id}"
            data-toggle="tooltip"
            data-placement="left"
            title="Edit"
            class="
              btn
              btn-sm
              btn-default
              btn-trans
              waves-effect
              js-edit-data
            "
            data-id="${data.id}"
          >
            <i class="ph ph-pencil-simple"></i>
          </a>


          <button
            type="button"
            data-toggle="tooltip"
            data-placement="left"
            title="Delete"
            class="
              btn
              btn-sm
              btn-danger
              btn-trans
              waves-effect
              js-delete-data
            "
            data-id="${data.id}"
          >
            <i class="ph ph-trash"></i>
          </button>
        `;

          const mobileDropdown = `
          <div
            class="
              dropdown
              d-block
              d-md-none
              action-dropdown
            "
          >

            <button
              class="
                btn
                btn-sm
                btn-light
                dropdown-toggle
                js-action-menu
              "
              type="button"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <i class="ph ph-dots-nine"></i>
            </button>


            <div
              class="
                dropdown-menu
                dropdown-menu-right
              "
            >

              <button
                class="
                  dropdown-item
                  js-print-data
                "
                data-id="${data.id}"
              >
                <i class="ph ph-printer"></i>

                Print
              </button>


              <button
                class="
                  dropdown-item
                  js-detail-data
                "
                data-id="${data.id}"
              >
                <i class="ph ph-magnifying-glass-plus"></i>

                Detail
              </button>


              <a
                href="${API_URL.order}/edit/${data.id}"
                class="
                  dropdown-item
                  js-edit-data
                "
                data-id="${data.id}"
              >
                <i class="ph ph-pencil-simple"></i>

                Edit
              </a>


              <button
                class="
                  dropdown-item
                  js-delete-data
                  text-danger
                "
                data-id="${data.id}"
              >
                <i class="ph ph-trash"></i>

                Delete
              </button>

            </div>

          </div>
        `;

          return `
          <div class="action-order">

            <div
              class="d-none d-md-flex"
              style="gap: 6px;"
            >
              ${buttonHtml}
            </div>

            ${mobileDropdown}


            <div class="action-order-detail">

              <p>
                <label>
                  Customer
                </label>

                ${data.customer}
              </p>

              ${timelineHtml}

            </div>

          </div>
        `;
        },
      },
    ];

    // =========================
    // Column Visibility
    // =========================

    let columnVisbleSetting = {
      target: [2, 3],

      visble: true,
    };

    const toHide = [];

    if ($(".js-customer-detail").length) {
      toHide.push(2);
    }

    if ($(".js-reseller-detail").length) {
      toHide.push(3);
    }

    if (toHide.length > 0) {
      columnVisbleSetting = {
        target: toHide,

        visble: false,
      };
    }

    // =========================
    // Initialize DataTable
    // =========================

    DataTable.custom(
      dataSetting,
      filterSetting,
      columnSetting,
      0,
      columnVisbleSetting
    );

    // =========================
    // Restore Search & Page
    // =========================

    if (savedState) {
      const tableSelector = ".js-data-order";

      // Tunggu DataTable benar-benar terbentuk
      setTimeout(() => {
        if (!$.fn.DataTable.isDataTable(tableSelector)) {
          return;
        }

        const table = $(tableSelector).DataTable();

        // Restore search DataTables
        if (savedState.filters?.search) {
          table.search(savedState.filters.search);
        }

        // Reload AJAX dahulu supaya filter custom
        // sudah masuk ke request
        table.ajax.reload(
          function () {
            if (savedState.page !== undefined && savedState.page !== null) {
              const pageInfo = table.page.info();

              // Kalau setelah edit jumlah halaman berubah,
              // jangan sampai request page yang sudah tidak ada.
              const targetPage = Math.min(
                savedState.page,
                Math.max(pageInfo.pages - 1, 0)
              );

              table.page(targetPage).draw("page");
            }

            // State hanya dipakai sekali,
            // yaitu saat kembali dari Edit.
            sessionStorage.removeItem(ORDER_TABLE_STATE_KEY);
          },

          false
        );
      }, 100);
    }
  };

  // =========================
  // Save State Before Edit
  // =========================

  $(document)
    .off("click.orderSaveState", ".js-edit-data")
    .on("click.orderSaveState", ".js-edit-data", function () {
      const tableSelector = ".js-data-order";

      let currentPage = 0;

      let dataTableSearch = "";

      if ($.fn.DataTable.isDataTable(tableSelector)) {
        const table = $(tableSelector).DataTable();

        currentPage = table.page();

        dataTableSearch = table.search();
      }

      const state = {
        page: currentPage,

        filters: {
          search: $("#search").val() || dataTableSearch || "",

          dateRange: $("#dateRange").val() || "",

          status: $("#status").val() || "",

          statusMobile: $("#statusMobile").val() || "",

          type: $("#type").val() || "",

          customer_id: $("#customer_id").val() || "",

          reseller_id: $("#reseller_id").val() || "",
        },
      };

      sessionStorage.setItem(ORDER_TABLE_STATE_KEY, JSON.stringify(state));
    });

  const handlePrintData = () => {
    $("body").on("click", ".js-print-data", (e) => {
      e.preventDefault();
      const id = $(e.currentTarget).data("id");
      console.log(id);
      swal({
        title: "Are you sure?",
        text: "Print this order?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-success",
        cancelButtonClass: "btn btn-danger m-l-10",
        buttonsStyling: false,
      }).then((willDelete) => {
        if (willDelete) {
          $("#dataTable").DataTable().ajax.reload();
          window.open(`${API_URL.order}/print/${id}`, "_blank");
        }
      });
    });
  };

  const handlePrintAllData = () => {
    $("body").on("click", ".js-print-all-data", (e) => {
      e.preventDefault();

      const ids = $(".js-select-data:checked")
        .map((_, el) => $(el).val())
        .get();

      if (ids.length === 0) {
        swal(
          "No rows selected",
          "Please select at least one order to print.",
          "warning"
        );
        return;
      }

      swal({
        title: "Are you sure?",
        text: "Print selected orders?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-success",
        cancelButtonClass: "btn btn-danger m-l-10",
        buttonsStyling: false,
      }).then((willPrint) => {
        if (willPrint) {
          // optional: refresh datatable before printing
          $("#dataTable").DataTable().ajax.reload();

          // build and submit form with ids[] to avoid URL limits
          const $form = $("<form>", {
            method: "POST",
            action: `${API_URL.order}/print`,
            target: "_blank",
          }).css("display", "none");

          ids.forEach((id) => {
            $form.append(
              $("<input>", { type: "hidden", name: "ids[]", value: id })
            );
          });

          $("body").append($form);
          $form.submit();
          $form.remove();
        }
      });
    });
  };

  // -----------------------
  // Validation wiring, submit enable/disable and submit handler
  // -----------------------
  // Helper: get submit button (unchanged)
  const getSubmitButton = () => {
    const $btn = $(".js-form-order button[type='submit']");
    if ($btn.length) return $btn;
    const formEl = document.querySelector(".js-form-order");
    if (formEl) {
      const btn = formEl.querySelector("button[type='submit']");
      return btn ? $(btn) : $();
    }
    return $();
  };

  // Check for validation markers (unchanged)
  const hasValidationErrors = () => {
    if ($(".js-form-order .error").length) return true;
    if ($(".js-form-order [aria-invalid='true']").length) return true;
    if ($(".js-form-order .is-invalid").length) return true;
    return false;
  };

  // Updated: check required fields including selector-based rules (e.g. quantity inputs)
  const requiredFieldsFilled = () => {
    if (!Array.isArray(ElementSelector)) return true; // can't check, allow

    for (let i = 0; i < ElementSelector.length; i++) {
      const sel = ElementSelector[i];
      if (!sel || !sel.id) continue;
      const validation = sel.validation || {};

      if (!validation.required) continue;

      // If rule provides a selector (multiple inputs, e.g. product qty) handle accordingly
      if (sel.selector) {
        const $els = $(sel.selector);
        // If the selector doesn't match any inputs, treat it as not filled
        if ($els.length === 0) return false;

        // For "required" we usually want every matched input to be filled (e.g. every quantity)
        let allFilled = true;
        $els.each(function () {
          const $el = $(this);
          const dataId = $el.attr("data-id");
          const val = (typeof $el.val === "function" ? $el.val() : "") || "";
          // If numeric + min constraints exist, validate them here briefly
          if (validation.numeric || typeof validation.min === "number") {
            const raw = String(val || "")
              .trim()
              .replace(/\s+/g, "")
              .replace(/,/g, ".");
            const n = Number(raw);
            if (
              !Number.isFinite(n) ||
              (typeof validation.min === "number" && n < validation.min)
            ) {
              allFilled = false;
              return false; // break
            }
          } else {
            if (!((dataId && String(dataId).trim()) || String(val).trim())) {
              allFilled = false;
              return false; // break
            }
          }
        });

        if (!allFilled) return false;
        continue; // next rule
      }

      // Default behavior: check by id first then fallback to name
      const id = sel.id;
      const $elById = $("#" + id);
      if ($elById.length) {
        const dataId = $elById.attr("data-id");
        const val =
          (typeof $elById.val === "function" ? $elById.val() : "") || "";
        if ((dataId && String(dataId).trim()) || String(val).trim()) {
          continue;
        }
        return false;
      }

      const el =
        document.getElementById(id) || document.querySelector(`[name="${id}"]`);
      if (el) {
        if (!String(el.value || "").trim()) return false;
      } else {
        // required element not present -> treat as not filled
        return false;
      }
    }

    return true;
  };

  // Update submit button enabled state
  const updateSubmitButtonState = () => {
    const $btn = getSubmitButton();
    if (!$btn || $btn.length === 0) return;
    const ok = !hasValidationErrors() && requiredFieldsFilled();
    $btn.prop("disabled", !ok);
  };

  // debounce helper (unchanged)
  const debounce = (fn, wait = 60) => {
    let t = null;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  // Setup wiring for submit state (updated to listen on selector-based rules)
  const setupSubmitState = () => {
    // initial evaluation
    updateSubmitButtonState();

    const debUpdate = debounce(updateSubmitButtonState, 80);

    // Determine which events to listen to. Fall back to common ones if ElementEvents not provided.
    const events =
      Array.isArray(ElementEvents) && ElementEvents.length
        ? ElementEvents
        : ["input", "change"];

    // Wire events for ElementSelector entries. Support both id-based and selector-based rules.
    try {
      if (Array.isArray(ElementSelector)) {
        ElementSelector.forEach((sel) => {
          if (!sel) return;
          // If rule supplies a selector (multiple inputs) bind to that selector
          if (sel.selector) {
            events.forEach((evt) => {
              $(document).on(evt, sel.selector, debUpdate);
            });
          } else if (sel.id) {
            events.forEach((evt) => {
              $(document).on(evt, `#${sel.id}`, debUpdate);
              // also bind to name attribute fallback
              $(document).on(evt, `[name="${sel.id}"]`, debUpdate);
            });
          }
        });
      }
    } catch (err) {
      // ignore wiring errors
      // console.warn('setupSubmitState wiring error', err);
    }

    // product row changes may affect totals/quantity -> watch them too
    $(document).on("input change", ".js-row-product [data-field]", debUpdate);

    // observe DOM class changes inside form (validator often toggles .error/.is-invalid/.aria-invalid)
    const formEl = document.querySelector(".js-form-order");
    if (formEl && window.MutationObserver) {
      const mo = new MutationObserver(debUpdate);
      mo.observe(formEl, {
        attributes: true,
        subtree: true,
        attributeFilter: ["class", "aria-invalid"],
      });
      // store observer for potential cleanup
      formEl.__submitStateObserver = mo;
    }

    // Re-evaluate on modal close / form reset
    $(document).on("click", 'button[data-dismiss="modal"]', () =>
      setTimeout(updateSubmitButtonState, 50)
    );
    $(document).on("reset", ".js-form-order", () =>
      setTimeout(updateSubmitButtonState, 50)
    );

    // If your validation library emits events after validation runs, hook them too
    $(document).on("validation:updated validation:checked", debUpdate);
  };

  // Initialize validation wiring (unchanged except we now support selector rules)
  const handleRunValidation = () => {
    if (typeof Form !== "undefined" && Form.validation) {
      Form.validation(ElementEvents, ElementSelector);
    }
    setupSubmitState();
  };

  // Submit click handler: blur all relevant elements (updated for selector rules), then proceed if no errors
  const handleClickValidation = () => {
    $(".js-form-order").on("click", "button[type='submit']", (e) => {
      e.preventDefault();
      const $btn = getSubmitButton();
      if ($btn.length && $btn.prop("disabled")) {
        return;
      }

      // trigger blur on all configured elements (id and selector)
      if (Array.isArray(ElementSelector)) {
        ElementSelector.forEach((sel) => {
          if (!sel) return;
          if (sel.selector) {
            $(sel.selector).each(function () {
              try {
                $(this).blur();
              } catch (err) {}
            });
          } else if (sel.id) {
            const $byId = $("#" + sel.id);
            if ($byId.length) $byId.blur();
            const $byName = $(`[name="${sel.id}"]`);
            if ($byName.length) $byName.blur();
          }
        });
      }

      // small timeout to allow validator to mark errors, then check
      setTimeout(() => {
        if (!hasValidationErrors()) {
          handlePostData();
        } else {
          // ensure button state updated
          updateSubmitButtonState();
        }
      }, 80);
    });
  };

  // -----------------------
  // Post form data (add/edit)
  // -----------------------
  const handlePostData = async () => {
    const formEl = document.querySelector(".js-form-order");
    if (!formEl) return;

    const $btn = $(".js-form-order button[type='submit']");

    // === LOADING STATE START ===
    // Disable button dan tambahkan class loading
    $btn.prop("disabled", true).addClass("is-loading");

    // Simpan teks asli button
    const originalText = $btn.text();

    // Ubah teks button menjadi loading
    $btn.html(
      '<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span> Processing...'
    );

    // Tambahkan overlay pada form (opsional)
    const $form = $(formEl);
    if (!$form.find(".form-overlay").length) {
      $form.append(
        '<div class="form-overlay" style="position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(255,255,255,0.7);z-index:999;display:flex;align-items:center;justify-content:center;border-radius:8px;"><div class="text-center"><div class="spinner-border text-primary" role="status"><span class="sr-only">Loading...</span></div><p class="mt-2">Saving order...</p></div></div>'
      );
      $form.css("position", "relative");
    }

    try {
      const fd = new FormData();

      const orderCodeEl = formEl.querySelector('[name="order_code"]');
      const manualOrderCodeCheckbox =
        document.getElementById("manualOrderCode");

      // Handle custom order code
      if (manualOrderCodeCheckbox && manualOrderCodeCheckbox.checked) {
        // Manual input - validate custom order code
        const customOrderCode = orderCodeEl ? orderCodeEl.value.trim() : "";

        if (!customOrderCode) {
          swal({
            title: "Warning!",
            text: "Please enter a custom order code.",
            type: "warning",
            confirmButtonClass: "btn btn-warning",
            timer: 2000,
          });
          // === LOADING STATE END (ERROR) ===
          resetLoadingState($btn, $form, originalText);
          return;
        }

        fd.append("order_code", customOrderCode);
        fd.append("is_manual_order_code", "1");
      } else {
        // Auto-generated - use the generated code
        if (orderCodeEl) {
          fd.append("order_code", orderCodeEl.value || "");
          fd.append("is_manual_order_code", "0");
        }
      }

      // ---- FIX: Validasi customer_id lebih ketat ----
      const $customerInput = $(".js-input-customer");
      let customerId = "";

      if ($customerInput.length) {
        customerId =
          $customerInput.attr("data-id") || $customerInput.val() || "";
      } else if (formEl.querySelector('[name="customer_id"]')) {
        customerId = formEl.querySelector('[name="customer_id"]').value || "";
      }

      // Trim dan validasi customer_id
      customerId = customerId.toString().trim();

      // FIX: Validasi ketat - pastikan customer_id tidak kosong dan berupa angka valid
      if (
        !customerId ||
        !/^\d+$/.test(customerId) ||
        parseInt(customerId) <= 0
      ) {
        swal({
          title: "Warning!",
          text: "Please select a valid customer before submitting.",
          type: "warning",
          confirmButtonClass: "btn btn-warning",
          timer: 3000,
        });
        // === LOADING STATE END (ERROR) ===
        resetLoadingState($btn, $form, originalText);
        return;
      }

      // Pastikan customer_id berupa integer
      fd.append("customer_id", parseInt(customerId, 10));

      // ---- Reseller ID dengan validasi serupa ----
      const $resellerInput = $(".js-input-reseller");
      let resellerId = "";
      if ($resellerInput.length) {
        resellerId =
          $resellerInput.attr("data-id") || $resellerInput.val() || "";
      } else if (formEl.querySelector('[name="reseller_id"]')) {
        resellerId = formEl.querySelector('[name="reseller_id"]').value || "";
      }

      resellerId = resellerId.toString().trim();
      if (resellerId && /^\d+$/.test(resellerId) && parseInt(resellerId) > 0) {
        fd.append("reseller_id", parseInt(resellerId, 10));
      }

      // ---- Fields lainnya dengan validasi ----
      const timeEl = formEl.querySelector(
        '[name="time"], [name="inbound_time"]'
      );
      if (timeEl && timeEl.value) fd.append("inbound_time", timeEl.value);

      const dateEl = formEl.querySelector(
        '[name="date"], [name="inbound_date"]'
      );
      if (dateEl && dateEl.value) fd.append("inbound_date", dateEl.value);

      const batchEl = formEl.querySelector('[name="batch_number"]');
      if (batchEl && batchEl.value) fd.append("batch_number", batchEl.value);

      const departureEl = formEl.querySelector(
        '[name="departure_date"], [name="depature_date"]'
      );
      if (departureEl && departureEl.value)
        fd.append("departure_date", departureEl.value);

      const notesEl = formEl.querySelector('[name="notes"], [name="note"]');
      if (notesEl && notesEl.value) fd.append("notes", notesEl.value);

      const phoneEl = formEl.querySelector('[name="phone"]');
      if (phoneEl && phoneEl.value) fd.append("phone", phoneEl.value);

      const orderIdEl = formEl.querySelector("#order_id");
      if (orderIdEl && orderIdEl.value) {
        fd.append("order_id", orderIdEl.value);
      }

      const isDuplicateData = formEl.querySelector(
        '[name="is_duplicate_data"]'
      );
      if (isDuplicateData) {
        fd.append("is_duplicate_data", isDuplicateData.checked ? "1" : "0");
      }

      // ---- products arrays and totals ----
      const productNameArr = [];
      const lengthArr = [];
      const widthArr = [];
      const heightArr = [];
      const weightArr = [];
      const qtyArr = [];
      const cbmArr = [];

      let totalQuantity = 0;
      let totalWeight = 0;
      let totalCBM = 0;

      const toNumberSafe = (v) => {
        if (typeof parseNumber === "function") return parseNumber(v);
        if (v === null || v === undefined) return 0;

        let s = String(v).trim();
        if (s === "") return 0;

        s = s.replace(/\s+/g, "");
        s = s.replace(/,/g, ".");
        s = s.replace(/[^0-9.\-]/g, "");

        const n = parseFloat(s);
        return Number.isFinite(n) ? n : 0;
      };

      // FIX: Tambahkan validasi minimal 1 product
      let hasValidProduct = false;

      $(".js-row-product").each(function () {
        const $row = $(this);
        if ($row.hasClass("product-summary-row")) return;

        const name = ($row.find("[data-field='name']").val() || "").trim();

        // FIX: Skip row jika product name kosong
        if (!name) return;

        const length = toNumberSafe($row.find("[data-field='length']").val());
        const width = toNumberSafe($row.find("[data-field='width']").val());
        const height = toNumberSafe($row.find("[data-field='height']").val());
        const weight = toNumberSafe($row.find("[data-field='weight']").val());
        const qty =
          parseInt($row.find("[data-field='quantity']").val() || 0, 10) || 0;

        // FIX: Skip jika quantity 0
        if (qty <= 0) return;

        let cbmField = toNumberSafe($row.find("[data-field='cbm']").val());

        let perUnitCbm = cbmField;
        if (
          (!perUnitCbm || perUnitCbm <= 0) &&
          length > 0 &&
          width > 0 &&
          height > 0
        ) {
          perUnitCbm = (length * width * height) / 1000000;
        }

        if (perUnitCbm > 0) {
          $row.find("[data-field='cbm']").val(Number(perUnitCbm).toFixed(3));
        } else {
          $row.find("[data-field='cbm']").val("");
        }

        const rowTotalCbm = perUnitCbm > 0 ? perUnitCbm : 0;

        totalQuantity += qty;
        totalWeight += weight * qty;
        totalCBM += rowTotalCbm * qty;

        productNameArr.push(name);
        lengthArr.push(length > 0 ? String(length) : "");
        widthArr.push(width > 0 ? String(width) : "");
        heightArr.push(height > 0 ? String(height) : "");
        weightArr.push(weight > 0 ? String(weight) : "");
        qtyArr.push(String(qty));
        cbmArr.push(perUnitCbm > 0 ? Number(perUnitCbm).toFixed(3) : "");

        hasValidProduct = true;
      });

      // FIX: Validasi minimal 1 produk
      if (!hasValidProduct) {
        swal({
          title: "Warning!",
          text: "Please add at least one product with name and quantity > 0.",
          type: "warning",
          confirmButtonClass: "btn btn-warning",
          timer: 3000,
        });
        // === LOADING STATE END (ERROR) ===
        resetLoadingState($btn, $form, originalText);
        return;
      }

      productNameArr.forEach((v) => fd.append("product_name[]", v));
      lengthArr.forEach((v) => fd.append("length_cm[]", v));
      widthArr.forEach((v) => fd.append("width_cm[]", v));
      heightArr.forEach((v) => fd.append("height_cm[]", v));
      weightArr.forEach((v) => fd.append("weight_kg[]", v));
      qtyArr.forEach((v) => fd.append("quantity[]", v));
      cbmArr.forEach((v) => fd.append("cbm[]", v));

      const totalWeightRounded = parseFloat(totalWeight.toFixed(1));
      const totalCBMRounded = parseFloat(totalCBM.toFixed(3));

      fd.append("total_qty", String(totalQuantity));
      fd.append("total_weight", String(totalWeightRounded));
      fd.append("total_cbm", String(totalCBMRounded));

      // update UI summary
      $(".js-summary-weight").val(totalWeightRounded.toFixed(1));
      $(".js-summary-quantity").val(totalQuantity);
      $(".js-summary-cbm").val(totalCBMRounded.toFixed(3));

      // ---- append photos ----
      if (Array.isArray(photosToRemove) && photosToRemove.length > 0) {
        photosToRemove.forEach((id) => fd.append("photos_remove[]", id));
      }

      if (Array.isArray(capturedPhotos)) {
        capturedPhotos.forEach((p, idx) => {
          if (!p || !p.src) return;

          if (!p.isServer) {
            try {
              const blob = dataURLtoBlob(p.src);
              fd.append("photos[]", blob, `photo_${idx + 1}.jpg`);
            } catch (err) {
              console.warn("Invalid captured photo", idx, err);
            }
          }
        });
      }

      const fileInputs = formEl.querySelectorAll(
        'input[type="file"][name^="photos"]'
      );
      fileInputs.forEach((input) => {
        Array.from(input.files || []).forEach((f) => fd.append("photos[]", f));
      });

      // FIX: Validasi foto minimal 1
      const hasPhotos =
        (Array.isArray(capturedPhotos) && capturedPhotos.length > 0) ||
        (fileInputs.length > 0 &&
          Array.from(fileInputs).some(
            (input) => input.files && input.files.length > 0
          ));

      if (!hasPhotos) {
        swal({
          title: "Warning!",
          text: "Please add at least one photo before submitting the order.",
          type: "warning",
          confirmButtonClass: "btn btn-warning",
          timer: 3000,
        });
        // === LOADING STATE END (ERROR) ===
        resetLoadingState($btn, $form, originalText);
        return;
      }

      // === UPDATE LOADING TEXT ===
      $btn.html(
        '<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span> Submitting order...'
      );

      let apiUrl = API_URL.orderAdd;
      if (orderIdEl && orderIdEl.value) apiUrl = API_URL.orderEdit;

      const payload = {
        url: apiUrl,
        method: "POST",
        data: fd,
        isFormData: true,
      };

      const result = await HttpRequest.custom(payload);
      console.log(result);

      if (result.code !== 200) {
        console.error("Server error", result);

        // FIX: Tampilkan error lebih detail
        let errorMessage = result.message || "Something went wrong";
        if (result.data && result.data.message) {
          errorMessage = result.data.message;
        }

        swal({
          title: "Error!",
          text: errorMessage,
          type: "error",
          confirmButtonClass: "btn btn-custom",
          timer: 5000,
        });

        // === LOADING STATE END (ERROR) ===
        resetLoadingState($btn, $form, originalText);
      } else {
        // === UPDATE LOADING TEXT ===
        $btn.html(
          '<span class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span> Success! Redirecting...'
        );

        swal({
          title: "Success!",
          text: result.message || "Order successfully created",
          type: "success",
          confirmButtonClass: "btn btn-custom",
          timer: 3000,
        });

        // FIX: Delay redirect untuk memastikan response tersimpan
        setTimeout(() => {
          window.location.href = API_URL.order;
        }, 1000);
      }
    } catch (error) {
      console.error("handlePostData error:", error);

      // FIX: Tampilkan error yang lebih informatif
      let errorMessage = "Something went wrong while submitting data.";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || errorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      swal({
        title: "Error!",
        text: errorMessage,
        type: "error",
        confirmButtonClass: "btn btn-custom",
        timer: 5000,
      });

      // === LOADING STATE END (ERROR) ===
      resetLoadingState($btn, $form, originalText);
    }
  };

  // === HELPER FUNCTION: Reset Loading State ===
  function resetLoadingState($btn, $form, originalText) {
    // Reset button
    $btn.prop("disabled", false).removeClass("is-loading");
    if (originalText) {
      $btn.text(originalText);
    } else {
      $btn.html("Submit");
    }

    // Remove overlay
    $form.find(".form-overlay").remove();
    $form.css("position", "");
  }

  // --- product naming / ids / labels AND delete button beside CBM
  const normalizeProductRow = ($row, index) => {
    $row.attr("data-index", index);

    $row.find("[data-field]").each(function () {
      const $el = $(this);
      const field = $el.attr("data-field");
      if (!field) return;
      $el.attr("name", `products[${index}][${field}]`);
      const id = `product-${index}-${field}`;
      $el.attr("id", id);

      const $formGroup = $el.closest(".form-group");
      if ($formGroup.length) {
        const $label = $formGroup.find("label").first();
        if ($label.length) {
          $label.attr("for", id);
        } else {
          const $prevLabel = $el.prevAll("label").first();
          if ($prevLabel.length) $prevLabel.attr("for", id);
        }
      } else {
        const $prevLabel = $el.prevAll("label").first();
        if ($prevLabel.length) $prevLabel.attr("for", id);
      }
    });

    // hide labels for rows other than first, keep for first
    if (index === 0) {
      $row.find("label").show();
    } else {
      $row.find("label").hide();
    }

    // Ensure delete button exists beside CBM input.
    // Remove any existing product-remove-inline to avoid duplicates
    $row.find(".product-remove-inline").remove();

    const $cbmInput = $row.find("[data-field='cbm']").first();
    let $cbmWrapper = $cbmInput.closest(".form-group");
    if ($cbmWrapper.length === 0) $cbmWrapper = $cbmInput.parent();

    const $removeWrap = $(
      '<div class="form-group product-remove-inline"></div>'
    );

    if (index > 0) {
      const $btn = $(
        `<button type="button" class="btn btn-sm btn-danger js-remove-product" data-index="${index}" title="Delete" style="width:40px;height:40px;border-radius:8px;"><i class="ph ph-trash"></i></button>`
      );
      $removeWrap.append($btn);
    } else {
      // placeholder to keep layout aligned
      $removeWrap.append(
        '<div style="width:40px;height:40px;display:inline-block;"></div>'
      );
    }

    if ($cbmWrapper.length) {
      $cbmWrapper.after($removeWrap);
    } else {
      $row.append($removeWrap);
    }
  };

  const renumberAllProductRows = () => {
    $(".js-row-product").each(function (i) {
      normalizeProductRow($(this), i);
    });
  };

  const updateProductSummary = () => {
    // Robust number parser: accepts "1.23", "1,23", "1 234,56", "1234", etc.
    const toNumber = (val) => {
      if (val === null || val === undefined) return 0;
      let s = String(val).trim();
      if (s === "") return 0;
      s = s.replace(/\s+/g, ""); // remove spaces (thousands separators)
      s = s.replace(/,/g, "."); // accept comma decimals
      s = s.replace(/[^0-9.\-\.]/g, ""); // strip anything else
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : 0;
    };

    let totalWeight = 0;
    let totalQuantity = 0;
    let totalCBM = 0;
    let productCount = 0;

    $(".js-row-product").each(function () {
      const $row = $(this);
      if ($row.hasClass("product-summary-row")) return;

      productCount++;

      const length = toNumber($row.find("[data-field='length']").val());
      const width = toNumber($row.find("[data-field='width']").val());
      const height = toNumber($row.find("[data-field='height']").val());
      const weight = toNumber($row.find("[data-field='weight']").val()); // per-unit weight

      // qty (kept as your original behavior: blank/invalid => 0)
      const quantity =
        Number.parseInt($row.find("[data-field='quantity']").val() || 0, 10) ||
        0;

      totalQuantity += quantity;

      // Field value (may be user-filled); still compute per-unit from dims for live feedback
      const cbmFieldRaw = $row.find("[data-field='cbm']").val();
      const cbmField = toNumber(cbmFieldRaw);

      let perUnitFromDims = 0;
      if (length > 0 && width > 0 && height > 0) {
        perUnitFromDims = (length * width * height) / 1000000; // cubic meters
      }

      // Pick per-unit cbm: prefer computed (from dims) when available, otherwise user-entered
      let perUnitCbm = 0;
      if (perUnitFromDims > 0) {
        perUnitCbm = perUnitFromDims;
        $row.find("[data-field='cbm']").val(Number(perUnitFromDims).toFixed(3));
      } else if (cbmField > 0) {
        perUnitCbm = cbmField;
        $row.find("[data-field='cbm']").val(Number(cbmField).toFixed(3));
      } else {
        $row.find("[data-field='cbm']").val("");
        perUnitCbm = 0;
      }

      // Totals should be per-unit * qty, using the displayed per-unit rounding so totals match the row display
      const perUnitCbmRounded = Number(perUnitCbm.toFixed(3));
      totalCBM += perUnitCbmRounded * quantity;

      // Weight total should be per-unit * qty
      totalWeight += weight * quantity;
    });

    // Round totals for display
    const totalWeightRounded = parseFloat(totalWeight.toFixed(1));
    const totalCBMRounded = parseFloat(totalCBM.toFixed(3));

    // Update summary fields (assumes formatNumber helper exists)
    $(".js-summary-count").val(
      `${productCount} Product${productCount !== 1 ? "s" : ""}`
    );
    $(".js-summary-weight").val(formatNumber(totalWeightRounded, 1));
    $(".js-summary-quantity").val(String(totalQuantity));
    $(".js-summary-cbm").val(formatNumber(totalCBMRounded, 3));
  };

  // wire input listeners (ensure this runs once)
  $("body").on(
    "input change",
    ".js-row-product [data-field='length'], .js-row-product [data-field='width'], .js-row-product [data-field='height'], .js-row-product [data-field='cbm'], .js-row-product [data-field='quantity']",
    function () {
      const $row = $(this).closest(".js-row-product");

      // If dimensions changed, recompute per-unit CBM and write to cbm field (live feedback)
      if (
        $(this).is("[data-field='length']") ||
        $(this).is("[data-field='width']") ||
        $(this).is("[data-field='height']")
      ) {
        const toNumber = (val) => {
          if (val === null || val === undefined) return 0;
          let s = String(val).trim();
          if (s === "") return 0;
          s = s.replace(/\s+/g, "");
          s = s.replace(/,/g, ".");
          s = s.replace(/[^0-9.\-]/g, "");
          const n = parseFloat(s);
          return Number.isFinite(n) ? n : 0;
        };
        const length = toNumber($row.find("[data-field='length']").val());
        const width = toNumber($row.find("[data-field='width']").val());
        const height = toNumber($row.find("[data-field='height']").val());

        if (length > 0 && width > 0 && height > 0) {
          const perUnit = (length * width * height) / 1000000;
          $row.find("[data-field='cbm']").val(Number(perUnit).toFixed(3));
        } else {
          // if dims incomplete, clear cbm so user isn't shown stale value
          // (optional: comment out to keep last computed value)
          $row.find("[data-field='cbm']").val("");
        }
      }

      // After any change recalc full summary
      updateProductSummary();
    }
  );

  // --- add product row (with remove) and change listeners
  const handleAddProductRow = () => {
    $("body").on("click", ".js-add-product", (e) => {
      e.preventDefault();
      const $last = $(".js-row-product").last();
      if ($last.length === 0) return;

      const $clone = $last.clone(false);
      $clone.find("input, textarea").each(function () {
        $(this).val("");
      });
      $clone.find("select").each(function () {
        $(this).prop("selectedIndex", 0);
      });

      $last.after($clone);

      renumberAllProductRows();
      updateProductSummary();
    });

    // recalc cbm when dims change and update summary
    $("body").on("input change", ".js-row-product [data-field]", function () {
      const $row = $(this).closest(".js-row-product");
      if (
        $(this).is("[data-field='length']") ||
        $(this).is("[data-field='width']") ||
        $(this).is("[data-field='height']") ||
        $(this).is("[data-field='cbm']")
      ) {
        const length = parseNumber($row.find("[data-field='length']").val());
        const width = parseNumber($row.find("[data-field='width']").val());
        const height = parseNumber($row.find("[data-field='height']").val());
        const cbmField = parseNumber($row.find("[data-field='cbm']").val());
        if (
          length > 0 &&
          width > 0 &&
          height > 0 &&
          (!cbmField || cbmField === 0)
        ) {
          const perUnit = (length * width * height) / 1000000;
          // write computed per-item CBM so user sees value
          $row.find("[data-field='cbm']").val(Number(perUnit).toFixed(3));
        }
      }
      updateProductSummary();
    });

    // delete handler (delegated)
    $("body").on("click", ".js-row-product .js-remove-product", function (e) {
      e.preventDefault();
      const $btn = $(this);
      const $row = $btn.closest(".js-row-product");

      // if (!confirm("Remove this product?")) return;

      swal({
        title: "Are you sure?",
        text: "Remove this product?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-success",
        cancelButtonClass: "btn btn-danger m-l-10",
        buttonsStyling: false,
      }).then((willDelete) => {
        if (willDelete) {
          $row.remove();
          renumberAllProductRows();
          updateProductSummary();
        }
      });

      // $row.remove();
      // renumberAllProductRows();
      // updateProductSummary();
    });
  };

  // ===============================
  // CAMERA & GALLERY HANDLER
  // ===============================
  let cameraStream = null;
  let capturedPhotos = [];
  let photosToRemove = [];
  let editPhotoIndex = null;
  const MAX_PHOTOS = 5;

  const videoEl = document.getElementById("video");
  const canvasEl = document.getElementById("canvas");

  // Deteksi mobile device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  // ============== CAMERA FUNCTIONS ==============
  const handleOpenCamera = () => {
    $("#modalCamera").on("shown.bs.modal", async () => {
      if (!cameraStream) {
        await startCamera();
      }

      // Tampilkan button switch camera hanya di mobile
      if (isMobileDevice()) {
        $("#btnSwitchCamera").show();
      } else {
        $("#btnSwitchCamera").hide();
      }
    });

    $("#modalCamera").on("hidden.bs.modal", () => {
      stopCamera();
      editPhotoIndex = null;
    });
  };

  const startCamera = async (facingMode = "environment") => {
    try {
      const constraints = {
        video: {
          facingMode: isMobileDevice() ? facingMode : "user",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      };

      cameraStream = await navigator.mediaDevices.getUserMedia(constraints);

      if (videoEl) {
        videoEl.srcObject = cameraStream;
        const wrapper = videoEl.closest(".camera-wrapper");
        if (wrapper) wrapper.style.display = "block";
        videoEl.hidden = false;
      }
    } catch (err) {
      console.error("Camera error:", err);
      alert("Camera tidak tersedia atau izin ditolak");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      cameraStream = null;
    }
    if (videoEl) {
      videoEl.srcObject = null;
    }
  };

  // Switch camera (front/back) untuk mobile
  const switchCamera = async () => {
    const currentFacing = videoEl?.srcObject
      ?.getVideoTracks()[0]
      ?.getSettings().facingMode;
    const newFacing = currentFacing === "environment" ? "user" : "environment";

    stopCamera();
    await startCamera(newFacing);
  };

  // ============== GALLERY FUNCTIONS ==============
  const openGallery = () => {
    const galleryInput = document.getElementById("galleryInput");

    // Reset value so same file can be selected again
    galleryInput.value = "";

    // Trigger click
    galleryInput.click();
  };

  const handleGallerySelection = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validasi file
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    // Validasi ukuran (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("File too large. Maximum 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const imageData = event.target.result;

      // Resize image untuk performance
      resizeImageIfNeeded(imageData, (resizedImage) => {
        // Proses seperti capture photo
        if (editPhotoIndex !== null) {
          // Jika replace server photo, mark for removal
          const existing = capturedPhotos[editPhotoIndex];
          if (existing && existing.isServer && existing.id) {
            photosToRemove.push(existing.id);
          }
          capturedPhotos[editPhotoIndex] = {
            src: resizedImage,
            id: null,
            isServer: false,
          };
          editPhotoIndex = null;
        } else {
          if (capturedPhotos.length >= MAX_PHOTOS) {
            alert(`Maximum ${MAX_PHOTOS} photos allowed`);
            return;
          }
          capturedPhotos.push({
            src: resizedImage,
            id: null,
            isServer: false,
          });
        }

        renderPhotoPreview();
        $("#modalCamera").modal("hide");
        stopCamera();
      });
    };

    reader.readAsDataURL(file);
  };

  // Resize image untuk optimasi
  const resizeImageIfNeeded = (imageData, callback) => {
    const img = new Image();
    img.onload = () => {
      const MAX_WIDTH = 1200;
      const MAX_HEIGHT = 1200;

      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      const resizedData = canvas.toDataURL("image/jpeg", 0.8);
      callback(resizedData);
    };
    img.src = imageData;
  };

  // ============== CAPTURE FUNCTIONS ==============
  const capturePhoto = () => {
    if (!canvasEl || !videoEl) return;

    canvasEl.width = videoEl.videoWidth;
    canvasEl.height = videoEl.videoHeight;

    const ctx = canvasEl.getContext("2d");
    ctx.drawImage(videoEl, 0, 0);

    const imageData = canvasEl.toDataURL("image/jpeg", 0.8);

    if (editPhotoIndex !== null) {
      // if replacing a server photo, mark it for removal
      const existing = capturedPhotos[editPhotoIndex];
      if (existing && existing.isServer && existing.id) {
        photosToRemove.push(existing.id);
      }
      capturedPhotos[editPhotoIndex] = {
        src: imageData,
        id: null,
        isServer: false,
      };
      editPhotoIndex = null;
    } else {
      if (capturedPhotos.length >= MAX_PHOTOS) return;
      capturedPhotos.push({
        src: imageData,
        id: null,
        isServer: false,
      });
    }

    renderPhotoPreview();
    $("#modalCamera").modal("hide");
  };

  // ============== RENDER FUNCTIONS ==============
  const renderPhotoPreview = () => {
    $(".preview-box").each(function (i) {
      if (capturedPhotos[i] && capturedPhotos[i].src) {
        const photo = capturedPhotos[i];
        $(this)
          .html(
            `
          <div class="photo-inner">
            <img src="${photo.src}" />
            <div class="photo-actions">
              <button type="button" class="btn-photo btn-edit" data-index="${i}" title="Edit">
                <i class="ph ph-pencil-simple"></i>
              </button>
              <button type="button" class="btn-photo btn-delete" data-index="${i}" title="Delete">
                <i class="ph ph-trash"></i>
              </button>
            </div>
          </div>
        `
          )
          .addClass("has-photo")
          .attr("data-photo-id", photo.isServer && photo.id ? photo.id : "");
      } else {
        $(this)
          .html(
            `<span class="photo-box-icon"><i class="ph ph-image"></i></span>
           <span class="photo-box-text">Preview</span>`
          )
          .removeClass("has-photo")
          .removeAttr("data-photo-id");
      }
    });
  };

  const initServerPhotos = () => {
    capturedPhotos = [];
    photosToRemove = [];
    editPhotoIndex = null;

    if (window.initialOrderPhotos && Array.isArray(window.initialOrderPhotos)) {
      window.initialOrderPhotos.slice(0, MAX_PHOTOS).forEach((p) => {
        capturedPhotos.push({
          src: p.src,
          id: p.id || null,
          isServer: Boolean(p.id),
        });
      });
    }

    if (capturedPhotos.length > MAX_PHOTOS)
      capturedPhotos = capturedPhotos.slice(0, MAX_PHOTOS);
    renderPhotoPreview();
  };

  // ============== EVENT LISTENERS ==============
  $(document).ready(function () {
    // Initialize
    handleOpenCamera();
    initServerPhotos();

    // Setup gallery input
    const galleryInput = document.getElementById("galleryInput");
    if (galleryInput) {
      galleryInput.onchange = handleGallerySelection;
    }
  });

  // Capture button
  $("body").on("click", "#btnCapture", () => {
    capturePhoto();
  });

  // Gallery button
  $("body").on("click", "#btnGallery", () => {
    openGallery();
  });

  // Switch camera button
  $("body").on("click", "#btnSwitchCamera", () => {
    switchCamera();
  });

  // Edit photo
  $("body").on("click", ".btn-edit", async function () {
    editPhotoIndex = $(this).data("index");
    $("#modalCamera").modal("show");
    if (!cameraStream) {
      await startCamera();
    }
  });

  // Delete photo
  $("body").on("click", ".btn-delete", function () {
    const index = $(this).data("index");
    const photo = capturedPhotos[index];
    if (photo && photo.isServer && photo.id) {
      photosToRemove.push(photo.id);
    }
    capturedPhotos.splice(index, 1);
    renderPhotoPreview();
  });

  // Click empty preview box to add photo
  $("body").on("click", ".preview-box:not(.has-photo)", function () {
    const idx = $(this).data("index");
    if (typeof idx === "number") {
      editPhotoIndex = idx;
      $("#modalCamera").modal("show");
    }
  });

  // Close modal button
  $('button[data-dismiss="modal"]').on("click", () => {
    stopCamera();
    editPhotoIndex = null;
  });

  // -----------------------------
  // Scanning (camera + keyboard) - gather-only on scan, batch save on Next
  // - Camera stays running after each scan.
  // - Scans only gather order data into scannedOrders (no server updates per-scan).
  // - Clicking "Next" will send the selected codes and their target statuses in one batch to the server.
  // - After batch save the UI marks each scanned item with saveStatus and shows a summary modal.
  // -----------------------------
  const handleScan = (() => {
    const modalSel = "#modalScanQRCode";
    const regionId = "html5-qrcode-region";
    const resultsContainer = "#scan-results-container";
    const statusText = "#scan-status-text";
    const cdnUrl =
      "https://unpkg.com/html5-qrcode@2.3.7/minified/html5-qrcode.min.js";
    const localUrl =
      (typeof BASE_URL !== "undefined" ? BASE_URL : "") +
      "assets/vendor/html5-qrcode.min.js";

    let html5QrcodeScanner = null;
    let lastScanned = null;
    let scanStep = "scan"; // "scan" or "confirm"
    const MAX_SCANS = 10;

    let scannedOrders = [];
    const processingCodes = new Set();

    const loadScript = (url) =>
      new Promise((resolve, reject) => {
        if (typeof Html5Qrcode !== "undefined") return resolve();
        if (document.querySelector(`script[src="${url}"]`)) {
          let i = 0;
          const wait = setInterval(() => {
            if (typeof Html5Qrcode !== "undefined") {
              clearInterval(wait);
              resolve();
            }
            if (++i > 50) {
              clearInterval(wait);
              reject(new Error("timeout"));
            }
          }, 100);
          return;
        }
        const s = document.createElement("script");
        s.src = url;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("Failed to load " + url));
        document.head.appendChild(s);
      });

    const ensureLib = async () => {
      if (typeof Html5Qrcode !== "undefined") return;
      try {
        await loadScript(cdnUrl);
        if (typeof Html5Qrcode !== "undefined") return;
      } catch (e) {
        console.warn("CDN failed, trying local", e);
      }
      await loadScript(localUrl);
    };

    const start = async () => {
      try {
        await ensureLib();
      } catch (err) {
        alert("Scanner library not available.");
        return;
      }

      if (html5QrcodeScanner) return;
      html5QrcodeScanner = new Html5Qrcode(regionId);

      const config = { fps: 8, qrbox: { width: 500, height: 500 } };

      try {
        await html5QrcodeScanner.start(
          { facingMode: "environment" },
          config,
          (decodedText) => {
            if (!decodedText) return;
            if (decodedText === lastScanned) return;
            lastScanned = decodedText;
            setTimeout(() => {
              if (lastScanned === decodedText) lastScanned = null;
            }, 1200);

            $(statusText).text("Scanned: " + decodedText);
            _onScannedCode(decodedText);
          },
          (err) => {
            // ignore decode errors
          }
        );
      } catch (err) {
        console.error("Failed to start scanner", err);
        alert("Unable to start camera.");
      }
    };

    const stop = () => {
      if (!html5QrcodeScanner) return;
      html5QrcodeScanner
        .stop()
        .then(() => {
          html5QrcodeScanner.clear();
          html5QrcodeScanner = null;
        })
        .catch((err) => {
          console.warn("stop scanner failed", err);
          html5QrcodeScanner = null;
        });
    };

    const nextStatusFor = (current) => {
      const steps = ["INBOUND", "LABELED", "OUTBOUND", "COMPLETED"];
      const cur = String(current || "INBOUND").toUpperCase();
      let idx = steps.indexOf(cur);
      if (idx === -1 && /^\d+$/.test(cur)) {
        idx = Math.max(0, Math.min(parseInt(cur, 10) - 1, steps.length - 1));
      }
      idx = Math.max(0, idx);
      const nextIdx = Math.min(steps.length - 1, idx + 1);
      return steps[nextIdx];
    };

    const lookupCode = (code) =>
      $.ajax({
        url: API_URL.orderScan,
        method: "POST",
        data: { code },
        dataType: "json",
      });

    const _onScannedCode = async (rawCode) => {
      if (scanStep === "confirm") return; // block camera scan in confirm state
      const code = String(rawCode || "").trim();
      if (!code) return;
      if (processingCodes.has(code)) return;
      if (scannedOrders.length >= MAX_SCANS) return;
      processingCodes.add(code);
      $(statusText).text("Looking up...");

      try {
        const res = await lookupCode(code);
        if (res && res.code === 200 && res.data) {
          const orders = res.data.orders;
          const list = Array.isArray(orders) ? orders : [orders];
          for (const order of list) {
            addScannedOrder(order);
          }
          $(modalSel).modal("show");
          renderScannedList();
          $(statusText).text("Order(s) added — click Next to save batch");
        } else {
          $(statusText).text("Order not found. Keep scanning...");
        }
      } catch (err) {
        console.error("lookup failed", err);
        $(statusText).text("Lookup error");
      } finally {
        processingCodes.delete(code);
      }
    };

    const addScannedOrder = (order) => {
      if (!order) return;
      if (scannedOrders.length >= MAX_SCANS) return;
      const order_code = (order.order_code || order.code || "").toString();
      if (!order_code) return;
      const exists = scannedOrders.find((s) => s.order_code === order_code);
      const currentStatus = (order.status || order.state || "INBOUND")
        .toString()
        .toUpperCase();
      const target = nextStatusFor(currentStatus);

      if (exists) {
        exists.customer_name =
          order.customer_name || order.customer || exists.customer_name;
        exists.status = currentStatus || exists.status;
        exists.targetStatus = target;
        exists.status_logs = order.status_logs || exists.status_logs || [];
        exists.saveStatus = null;
        exists.saveMessage = null;
        return;
      }

      scannedOrders.push({
        order_code,
        id: order.id || order.order_id || null,
        customer_name: order.customer_name || order.customer || "",
        status: currentStatus,
        targetStatus: target,
        status_logs: Array.isArray(order.status_logs) ? order.status_logs : [],
        selected: true,
        saveStatus: null,
        saveMessage: null,
      });
    };

    // Renders the scanned list and hides/shows camera region accordingly
    const renderScannedList = () => {
      const $c = $(resultsContainer).empty();

      // Handle .scan-results class for confirm mode
      const $results = $(".scan-results");
      if (scanStep === "confirm") {
        $results.addClass("confirm");
      } else {
        $results.removeClass("confirm");
      }

      if (scanStep === "scan") {
        $("#" + regionId).show();
      } else {
        $("#" + regionId).hide();
      }

      if (!Array.isArray(scannedOrders) || scannedOrders.length === 0) {
        $c.html('<div class="text-muted">No scanned orders yet</div>');
        renderScanFooter();
        return;
      }

      scannedOrders.forEach((s, idx) => {
        const steps = ["INBOUND", "LABELED", "OUTBOUND", "COMPLETED"];
        let activeIndex = steps.indexOf(s.targetStatus || s.status);
        if (activeIndex === -1 && /^\d+$/.test(s.targetStatus || s.status)) {
          const num = Math.max(
            1,
            Math.min(parseInt(s.targetStatus || s.status, 10), steps.length)
          );
          activeIndex = num - 1;
        }
        if (activeIndex === -1) activeIndex = 0;

        let timelineHtml =
          '<div class="timeline-steps lite" style="margin:0;">';
        steps.forEach((st, i) => {
          const isTarget = st === (s.targetStatus || "");
          const cls = i <= activeIndex ? "active" : "";
          const extra = isTarget
            ? " style='box-shadow:0 0 0 6px rgba(255,138,0,0.06);border-color:#ff8a00;'"
            : "";
          timelineHtml += `<div class="timeline-steps-item ${cls}" title="${st}"><span class="icon"${extra}><i class="ph ${
            i === 0
              ? "ph-package"
              : i === 1
              ? "ph-printer"
              : i === 2
              ? "ph-car"
              : "ph-check"
          }"></i></span></div>`;
        });
        timelineHtml += "</div>";

        let saveBadge = "";
        if (s.saveStatus === "saved") {
          saveBadge = `<span class="badge badge-success ml-2" title="${escapeHtml(
            s.saveMessage || ""
          )}">Saved</span>`;
        } else if (s.saveStatus === "skipped") {
          saveBadge = `<span class="badge badge-secondary ml-2" title="${escapeHtml(
            s.saveMessage || ""
          )}">Skipped</span>`;
        } else if (s.saveStatus === "error") {
          saveBadge = `<span class="badge badge-danger ml-2" title="${escapeHtml(
            s.saveMessage || ""
          )}">Error</span>`;
        }

        const $row = $(`
        <div class="scan-item" data-idx="${idx}">
          <div style="display:flex;gap:12px;align-items:center;flex:1;">
            <div style="flex:1;">
              <div style="display:flex;justify-content:space-between;align-items:center;">
                <div style="font-weight:600">${escapeHtml(
                  s.customer_name || ""
                )} ${saveBadge}</div>
                <div style="color:#999">${escapeHtml(s.order_code)}</div>
              </div>
              <div style="margin-top:8px;">
                ${timelineHtml}
              </div>
            </div>
            <div style="width:120px;text-align:center;">
              <div style="font-size:0.85rem;color:#666;margin-bottom:12px;">Next: <strong>${escapeHtml(
                s.targetStatus || ""
              )}</strong></div>
              <input type="hidden" class="btn btn-sm btn-light btn-toggle-select" data-idx="${idx}">
              <button type="button" class="btn btn-sm btn-outline-danger btn-remove-scan" data-idx="${idx}"><i class="ph ph-trash"></i></button>
            </div>
          </div>
        </div>
      `);
        $c.append($row);
      });

      renderScanFooter();
    };

    const renderScanFooter = () => {
      const $modal = $(modalSel);
      let $footer = $modal.find(".scan-modal-footer");
      if ($footer.length === 0) {
        $footer = $(
          `<div class="scan-modal-footer" style="display:flex;justify-content:space-between;padding:12px;border-top:1px solid #eee;margin-top:12px;"></div>`
        );
        $(resultsContainer).after($footer);
      }
      const selected = scannedOrders.filter((s) => s.selected).length;

      const isConfirm = scanStep === "confirm";
      $footer.html(`
        <div>
          <button type="button" class="btn btn-custom btn-trans js-scan-cancel">Cancel</button>
        </div>
        <div style="display:flex;align-items:center;">
          <div class="scan-footer-counter${
            isConfirm ? " scan-footer-counter-max" : ""
          }">
            <span class="scan-footer-current${
              isConfirm ? " scan-footer-current-max" : ""
            }">${selected}</span>
            <span class="scan-footer-slash">/</span>
            <span class="scan-footer-max${
              isConfirm ? " scan-footer-max-max" : ""
            }">${MAX_SCANS}</span>
          </div>
          <button type="button" class="btn btn-custom js-scan-next" ${
            !selected ? "disabled" : ""
          }>${isConfirm ? "Confirm" : "Next"}</button>
        </div>
      `);
    };

    $(document).on("click", ".btn-toggle-select", function () {
      const idx = parseInt($(this).attr("data-idx"), 10);
      if (isNaN(idx)) return;
      scannedOrders[idx].selected = !scannedOrders[idx].selected;
      renderScannedList();
    });

    $(document).on("click", ".btn-remove-scan", function () {
      const idx = parseInt($(this).attr("data-idx"), 10);
      if (isNaN(idx)) return;
      scannedOrders.splice(idx, 1);
      renderScannedList();
    });

    $(document).on("click", ".js-scan-cancel", function () {
      if (scanStep === "confirm") {
        scanStep = "scan";
        start(); // Show camera again
        renderScannedList();
        return;
      }
      scannedOrders = [];
      renderScannedList();
      $(modalSel).modal("hide");
    });

    const showSummaryModal = (summaryHtml, title = "Scan result") => {
      let $m = $("#scanSummaryModal");
      if ($m.length === 0) {
        $m = $(`
        <div id="scanSummaryModal" class="modal fade" tabindex="-1" role="dialog">
          <div class="modal-dialog modal-md modal-dialog-centered" role="document">
            <div class="modal-content">
              <div class="modal-header"><h5 class="modal-title"></h5><button type="button" class="close" data-dismiss="modal">&times;</button></div>
              <div class="modal-body"></div>
              <div class="modal-footer"><button type="button" class="btn btn-custom" data-dismiss="modal">OK</button></div>
            </div>
          </div>
        </div>
      `).appendTo("body");
      }
      $m.find(".modal-title").text(title);
      $m.find(".modal-body").html(summaryHtml);
      $m.modal("show");
    };

    // THE KEY UPDATED HANDLER: Now Confirm hits API!
    $(document).on("click", ".js-scan-next", async function () {
      const $btn = $(this);
      const selectedItems = scannedOrders.filter((s) => s.selected);
      const selectedCodes = selectedItems.map((s) => s.order_code);
      const selectedStatuses = selectedItems.map(
        (s) => s.targetStatus || s.status
      );

      if (!selectedCodes.length) return alert("No orders selected");

      // Always switch to confirm mode and hide camera (when not in confirm mode)
      if (scanStep === "scan") {
        scanStep = "confirm";
        stop();
        renderScannedList();
        return;
      }
      // In confirm mode, do batch API call
      if (scanStep === "confirm") {
        $btn.prop("disabled", true).addClass("is-loading");
        const batchUrl = API_URL.orderScanBatch || API_URL.orderScan;

        try {
          const res = await $.ajax({
            url: batchUrl,
            method: "POST",
            data: {
              codes: selectedCodes,
              statuses: selectedStatuses,
              batch: true,
            },
            dataType: "json",
          });

          if (res && res.code === 200) {
            const processed =
              res.data && Array.isArray(res.data.processed_codes)
                ? res.data.processed_codes
                : selectedCodes;
            const results =
              res.data && res.data.results ? res.data.results : {};
            const errors = res.data && res.data.errors ? res.data.errors : {};

            selectedItems.forEach((it) => {
              const code = it.order_code;
              let r = null;
              if (results[code]) r = results[code];
              else {
                const maybe = Object.values(results).find(
                  (x) => x && (x.order_code === code || x.code === code)
                );
                if (maybe) r = maybe;
              }

              if (errors && errors[code]) {
                it.saveStatus = "error";
                it.saveMessage = errors[code];
              } else if (
                r &&
                r.status === "COMPLETED" &&
                (it.status === "COMPLETED" || it.targetStatus === "COMPLETED")
              ) {
                it.saveStatus = "skipped";
                it.saveMessage = r.message || "Already completed";
              } else if (processed.includes(code)) {
                it.saveStatus = "saved";
                it.saveMessage =
                  r && r.message
                    ? r.message
                    : r && r.status
                    ? `Set to ${r.status}`
                    : "Saved";
              } else {
                it.saveStatus = "saved";
                it.saveMessage = r && r.message ? r.message : "Saved";
              }
            });

            let savedCount = scannedOrders.filter(
              (s) => s.saveStatus === "saved"
            ).length;
            let skippedCount = scannedOrders.filter(
              (s) => s.saveStatus === "skipped"
            ).length;
            let errorCount = scannedOrders.filter(
              (s) => s.saveStatus === "error"
            ).length;

            let html = `<p>Processed: <strong>${processed.length}</strong></p>
              <ul style="max-height:300px;overflow:auto;padding-left:1rem">`;
            selectedItems.forEach((it) => {
              const badge =
                it.saveStatus === "saved"
                  ? `<span style="color:green">Saved</span>`
                  : it.saveStatus === "skipped"
                  ? `<span style="color:#777">Skipped</span>`
                  : it.saveStatus === "error"
                  ? `<span style="color:red">Error</span>`
                  : `<span>—</span>`;
              html += `<li style="margin-bottom:6px;"><strong>${escapeHtml(
                it.order_code
              )}</strong> — ${badge} ${
                it.saveMessage ? "- " + escapeHtml(it.saveMessage) : ""
              }</li>`;
            });
            html += `</ul><p>Saved: ${savedCount}, Skipped: ${skippedCount}, Errors: ${errorCount}</p>`;

            if (res.data && Array.isArray(res.data.processed_codes)) {
              scannedOrders = scannedOrders.filter(
                (s) => !res.data.processed_codes.includes(s.order_code)
              );
            } else {
              scannedOrders = scannedOrders.filter((s) => !s.selected);
            }

            renderScannedList();
            $(statusText).text("Batch saved");
            showSummaryModal(html, "Batch result");
          } else {
            alert(res.message || "Failed to save batch");
          }
        } catch (err) {
          console.error("Batch save failed", err);
          alert("Failed to save batch. Check console for details.");
        } finally {
          $btn.prop("disabled", false).removeClass("is-loading");
        }
        return; // Important: End here in confirm mode!
      }

      // (nothing is needed here)
    });

    const renderResults = (orders) => {
      if (!Array.isArray(orders)) orders = [orders];
      orders.forEach((o) => addScannedOrder(o));
      renderScannedList();
    };

    function escapeHtml(s) {
      if (s === null || s === undefined) return "";
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    }

    let kbBuffer = "";
    let kbLastTime = 0;
    let kbListening = false;
    const kbInputId = "barcode-input-hidden";
    const kbMaxInterKey = 80;
    const kbMinLength = 3;

    const ensureKbInput = () => {
      let $inp = $("#" + kbInputId);
      if ($inp.length === 0) {
        $inp = $("<input/>", {
          type: "text",
          id: kbInputId,
          style: "position:absolute;left:-9999px;top:auto;opacity:0;",
          autocomplete: "off",
          autocorrect: "off",
          spellcheck: false,
        }).appendTo("body");
      }
      return $inp;
    };

    const kbHandleScanned = (code) => {
      const normalized = String(code || "").trim();
      if (!normalized || normalized.length < kbMinLength) return;
      _onScannedCode(normalized); // keep scanning
    };

    const kbKeyHandler = (e) => {
      const t = Date.now();
      if (["Shift", "Control", "Alt", "Meta"].includes(e.key)) return;
      if (kbLastTime && t - kbLastTime > kbMaxInterKey) kbBuffer = "";
      kbLastTime = t;
      if (e.key === "Enter") {
        if (kbBuffer.length >= kbMinLength) kbHandleScanned(kbBuffer);
        kbBuffer = "";
        e.preventDefault();
        return;
      }
      if (e.key && e.key.length === 1) kbBuffer += e.key;
    };

    const kbPasteHandler = (e) => {
      const pasted = (e.originalEvent || e).clipboardData.getData("text") || "";
      if (pasted) {
        const code = pasted.trim();
        if (code.length >= kbMinLength) kbHandleScanned(code);
        kbBuffer = "";
        e.preventDefault();
      }
    };

    const startKbListening = () => {
      if (kbListening) return;
      const $inp = ensureKbInput();
      $inp.on("keydown.kbscan", kbKeyHandler);
      $inp.on("paste.kbscan", kbPasteHandler);
      setTimeout(() => $inp.focus(), 100);
      kbListening = true;
    };

    const stopKbListening = () => {
      if (!kbListening) return;
      $("#" + kbInputId).off(".kbscan");
      kbBuffer = "";
      kbLastTime = 0;
      kbListening = false;
    };

    $(document).on("click", ".js-open-scan", function (e) {
      e.preventDefault();
      $(resultsContainer).empty();
      $(statusText).text("Ready to scan");
      lastScanned = null;
      scannedOrders = [];
      scanStep = "scan";
      renderScannedList();
      $(modalSel).modal("show");
    });

    $(modalSel).on("shown.bs.modal", function () {
      lastScanned = null;
      scanStep = "scan";
      start();
      startKbListening();
    });

    $(modalSel).on("hidden.bs.modal", function () {
      stop();
      stopKbListening();
    });

    return {
      init: () => {
        const local =
          (typeof BASE_URL !== "undefined" ? BASE_URL : "") +
          "assets/vendor/html5-qrcode.min.js";
        loadScript(local).catch(() => {});
      },
      startCamera: start,
      stopCamera: stop,
      startKeyboard: startKbListening,
      stopKeyboard: stopKbListening,
      getScannedOrders: () => scannedOrders,
    };
  })();

  $(document).on("click", ".js-export-data", function (e) {
    e.preventDefault();

    const picker = $("#dateRange").data("daterangepicker");

    let start_date = "";
    let end_date = "";

    if (picker) {
      start_date = picker.startDate.format("DD/MM/YYYY");
      end_date = picker.endDate.format("DD/MM/YYYY");
    }

    // Get filter type from input
    const _type = $("#type").val();

    const params = new URLSearchParams();

    if (start_date) params.set("start_date", start_date);
    if (end_date) params.set("end_date", end_date);
    if (_type) params.set("type", _type);

    console.log(start_date, end_date, _type);
    console.log(API_URL.exportData + "?" + params.toString());

    window.location.href = API_URL.exportData + "?" + params.toString();
  });

  // -----------------------------
  // Handle Get & Render Order Detail (drawer)
  // -----------------------------
  // Updated handleGetDetailData: timeline now uses log entries and shows scanned_by name.
  const handleGetDetailData = () => {
    // helper to escape HTML
    const escapeHtml = (s) => {
      if (s === null || s === undefined) return "";
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    };

    // render timeline using logs array (preferred) or fallback to status + dateText
    // improved renderTimeline: use logs order and last-log-per-status mapping
    const renderTimeline = (logsOrStatus, dateText) => {
      const steps = ["INBOUND", "LABELED", "OUTBOUND", "COMPLETED"];

      // helper: format date to "HH.mm, DD Mon YYYY" (e.g. "21.15, 02 Feb 2026")
      const formatLogDate = (input) => {
        if (!input) return "";
        // if already a Date
        let dt = input instanceof Date ? input : null;

        if (!dt) {
          // try to build Date from common formats
          // first try native parsing
          dt = new Date(input);
          if (isNaN(dt.getTime())) {
            // try 'YYYY-MM-DD HH:MM:SS' or 'YYYY-MM-DDTHH:MM:SS'
            const m = String(input).match(
              /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):?(\d{2})?/
            );
            if (m) {
              dt = new Date(
                m[1],
                Number(m[2]) - 1,
                m[3],
                m[4],
                m[5],
                m[6] || 0
              );
            } else {
              // last resort: return original string
              return String(input);
            }
          }
        }

        const pad = (n) => String(n).padStart(2, "0");
        const hh = pad(dt.getHours());
        const mm = pad(dt.getMinutes());
        const dd = pad(dt.getDate());
        const months = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const mon = months[dt.getMonth()] || "";
        const yyyy = dt.getFullYear();

        return `${hh}.${mm}, ${dd} ${mon} ${yyyy}`;
      };

      // normalize logs input: either an array of log objects or a single status string
      let logs = [];
      let fallbackStatus = null;
      if (Array.isArray(logsOrStatus)) {
        logs = logsOrStatus.slice(); // copy
      } else {
        fallbackStatus = String(logsOrStatus || "").toUpperCase() || null;
      }

      // If we have logs, try to sort them by scanned_at / created_at ascending
      if (logs.length) {
        logs.sort((a, b) => {
          const ta =
            new Date(
              a.scanned_at || a.created_at || a.created_date || 0
            ).getTime() || 0;
          const tb =
            new Date(
              b.scanned_at || b.created_at || b.created_date || 0
            ).getTime() || 0;
          return ta - tb;
        });
      }

      // Build a map of the last log per status (uppercased status key)
      const lastLogByStatus = {};
      logs.forEach((l) => {
        if (!l || !l.status) return;
        const key = String(l.status).toUpperCase();
        lastLogByStatus[key] = l; // keep last (most recent) occurrence for that status
      });

      // Determine active status:
      // - prefer last log entry's status (logs[logs.length-1])
      // - else fallbackStatus if provided
      // - else default to INBOUND
      let activeStatus = "INBOUND";
      if (logs.length) {
        const last = logs[logs.length - 1];
        if (last && last.status)
          activeStatus = String(last.status).toUpperCase();
      } else if (fallbackStatus) {
        activeStatus = fallbackStatus;
      }

      let activeIndex = Math.max(0, steps.indexOf(activeStatus));
      if (activeIndex === -1) activeIndex = 0;

      // Apply DOM updates for each timeline step element
      const $items = $("#orderDetailDrawer").find(".timeline-steps-item");
      $items.each(function (i) {
        const $li = $(this);
        const stepAttr = $li.attr("data-step") || steps[i] || "";
        const step = String(stepAttr).toUpperCase();

        // ensure .date and .scanned-by placeholders exist
        if ($li.find(".date").length === 0) {
          $li.append('<div class="date" style="display:none"></div>');
        }
        if ($li.find(".scanned-by").length === 0) {
          $li.append(
            '<div class="scanned-by" style="display:none;font-size:.85rem;color:#666;margin-top:.25rem"></div>'
          );
        }

        const $dateEl = $li.find(".date");
        const $scannedByEl = $li.find(".scanned-by");

        const log = lastLogByStatus[step];

        // mark active if this step index <= activeIndex
        if (i <= activeIndex) $li.addClass("active");
        else $li.removeClass("active");

        if (log) {
          // Prefer scanned_at, fallback to created_at/created_date
          const scannedAt =
            log.scanned_at || log.created_at || log.created_date || "";
          $dateEl.text(formatLogDate(scannedAt)).show();

          const scannerName =
            log.scanned_by_name || log.scanned_by || log.user_name || "";

          if (scannerName) {
            // Use the current step to decide the verb:
            // if this step is LABELED -> "Printed by", otherwise "Scanned by"
            const verb = step === "LABELED" ? "Printed by" : "Added by";
            $scannedByEl.text(`${verb} ${escapeHtml(scannerName)}`).show();
          } else {
            $scannedByEl.hide().text("");
          }
        } else {
          // No explicit log for this step
          // If INBOUND and we have a fallback dateText and no logs at all, show it
          if (step === "INBOUND" && logs.length === 0 && dateText) {
            $dateEl.text(formatLogDate(dateText)).show();
          } else {
            $dateEl.hide().text("");
          }
          $scannedByEl.hide().text("");
        }
      });
    };

    // render products into table body .js-products and update summary
    const renderProducts = (products) => {
      const $tbody = $(".js-products").empty();
      let totalQty = 0,
        totalWeight = 0,
        totalCbm = 0;
      products = Array.isArray(products) ? products : [];

      products.forEach((p) => {
        const name = escapeHtml(p.product_name || p.name || "");
        const length = p.length_cm ? `${escapeHtml(p.length_cm)} cm` : "";
        const width = p.width_cm ? `${escapeHtml(p.width_cm)} cm` : "";
        const height = p.height_cm ? `${escapeHtml(p.height_cm)} cm` : "";
        const weight = p.weight_kg ? `${escapeHtml(p.weight_kg)} kg` : "";
        const qty = Number(p.quantity || 0);
        const cbmText = p.cbm ? `${Number(p.cbm).toFixed(3)} m3` : "";

        const $tr = $("<tr/>")
          .append(`<td>${name}</td>`)
          .append(`<td>${length}</td>`)
          .append(`<td>${width}</td>`)
          .append(`<td>${height}</td>`)
          .append(`<td>${weight}</td>`)
          .append(`<td>${qty ? qty + " item" : ""}</td>`)
          .append(`<td>${cbmText}</td>`);

        $tbody.append($tr);

        totalQty += qty;
        totalWeight += Number(p.weight_kg || p.weight || 0) * qty;
        totalCbm += Number(p.cbm || 0) * qty;
      });

      $(".js-summary-count").text(
        `${products.length} Product${products.length !== 1 ? "s" : ""}`
      );
      $(".js-summary-weight").text(`${Number(totalWeight || 0).toFixed(1)} kg`);
      $(".js-summary-qty").text(`${totalQty} item`);
      $(".js-summary-cbm").text(`${Number(totalCbm || 0).toFixed(3)} m3`);
    };

    // render photos thumbnails and bind preview
    const renderPhotos = (photos) => {
      const $cont = $(".js-photos").empty();
      photos = Array.isArray(photos) ? photos : [];

      photos.forEach((ph, idx) => {
        let src = ph.full_url || ph.url || ph.src || "";
        if (!src && ph.photo_path) {
          src = BASE_URL + "/files/orders/" + ph.photo_path.replace(/^\//, "");
        }
        const $thumb = $(
          `<div class="photo-box preview-box">
          <div class="photo-inner" data-index="${idx}" title="Click to preview"><img src="${escapeHtml(
            src
          )}" alt="photo-${idx}"></div>
        </div>`
        );
        $thumb.on("click", () => {
          $("#orderPhotoPreviewImg").attr("src", src);
          $("#orderPhotoPreviewModal").modal("show");
        });
        $cont.append($thumb);
      });
    };

    // wire delegated click for detail buttons
    $("body")
      .off("click.orderDetail", ".js-detail-data")
      .on("click.orderDetail", ".js-detail-data", function (e) {
        e.preventDefault();
        console.log("=== Detail button clicked ===");

        const $btn = $(this);
        const orderId = $btn.data("id") || $btn.attr("data-id");
        if (!orderId) return;

        const $drawer = $("#orderDetailDrawer");
        // clear / loading state
        $(".js-detail-id").text("Loading...");
        $(".js-detail-customer").text("");
        $(".js-detail-phone").text("");
        $(".js-detail-reseller").text("");
        $(".js-detail-batch").text("");
        $(".js-detail-departure").text("");
        $(".js-detail-notes").val("");
        $(".js-products").empty();
        $(".js-photos").empty();
        $(".js-summary-count").text("0 Product");
        $(".js-summary-weight").text("0.0 kg");
        $(".js-summary-qty").text("0 item");
        $(".js-summary-cbm").text("0.000 m3");
        $("#orderDetailDrawer")
          .find(".timeline-steps li")
          .removeClass("active");
        $("#js-detail-qr").empty().text("QR");

        // show drawer
        if ($drawer && $drawer.length) $drawer.modal("show");

        // fetch order detail
        const fd = new FormData();
        fd.append("order_id", orderId);

        $.ajax({
          url: API_URL.orderGet,
          method: "POST",
          data: fd,
          processData: false,
          contentType: false,
          dataType: "json",
          beforeSend: () => {
            $drawer.addClass("loading");
          },
        })
          .done((res) => {
            $drawer.removeClass("loading");
            const order =
              res && res.data && res.data.order
                ? res.data.order
                : res && res.order
                ? res.order
                : res && res.data
                ? res.data
                : res;
            if (!order) {
              $(".js-detail-id").text("-");
              console.warn("Order not found in response", res);
              return;
            }

            // Information
            $(".js-detail-id").text(order.order_code || order.id || "-");
            $(".js-detail-customer").text(
              order.customer_name || order.customer || "-"
            );
            $(".js-detail-phone").text(
              order.customer_phone || order.phone || "-"
            );
            $(".js-detail-reseller").text(
              order.reseller_name || order.reseller || "-"
            );

            // Usage: replace your assignment with this
            const inboundTitle = FormatDate.inboundFormat(
              order.inbound_time,
              order.inbound_date
            );
            const fallback = order.order_code || order.id || "-";
            $(".js-order-photo-preview-title").text(inboundTitle || fallback);

            // $(".js-order-photo-preview-title").text(
            //   order.inbound_time + ", " + order.inbound_date
            // );

            // Shipment
            $(".js-detail-batch").text(order.batch_number || "-");
            $(".js-detail-departure").text(
              FormatDate.inboundFormat(false, order.departure_date) || "-"
            );

            // Notes
            $(".js-detail-notes").val(order.notes || "");

            // Timeline:
            // Prefer using order.log array fetched from backend (each log should include status, scanned_at, scanned_by_name)
            const logs = Array.isArray(order.log) ? order.log : [];
            // optionally: if scanned_by is user id and backend didn't include scanned_by_name, you can map users client-side if you have them
            const orderDateText =
              order.order_date ||
              (order.inbound_time && order.inbound_date
                ? `${order.inbound_time}, ${order.inbound_date}`
                : "");
            renderTimeline(
              logs.length ? logs : order.status || order.state || "INBOUND",
              orderDateText
            );

            // Products
            renderProducts(order.products || []);

            // Photos
            renderPhotos(order.photos || []);

            // QR (optional)
            if (order.qr_url || order.qr) {
              $("#js-detail-qr").html(
                `<img src="${escapeHtml(
                  order.qr_url || order.qr
                )}" alt="qr" style="max-width:100%;height:auto" />`
              );
            }

            // store current status on drawer for prev/next actions
            $drawer.data("order-id", orderId);
            $drawer.data(
              "order-status",
              (order.status || order.state || "INBOUND")
                .toString()
                .toUpperCase()
            );
          })
          .fail((xhr, status, err) => {
            $drawer.removeClass("loading");
            console.error("Failed to fetch order detail", err);
            alert("Failed to load order details");
          });
      });

    // Prev / Next step buttons inside drawer
    $("body")
      .off(
        "click.orderDetailStep",
        "#orderDetailDrawer .btn-prev, #orderDetailDrawer .btn-next"
      )
      .on(
        "click.orderDetailStep",
        "#orderDetailDrawer .btn-prev, #orderDetailDrawer .btn-next",
        function (e) {
          e.preventDefault();

          const $btn = $(this);
          const $drawer = $("#orderDetailDrawer");

          const orderId = $drawer.data("order-id");

          if (!orderId) return;

          const steps = ["INBOUND", "LABELED", "OUTBOUND", "COMPLETED"];

          let currentStatus = ($drawer.data("order-status") || "INBOUND")
            .toString()
            .toUpperCase();

          let idx = Math.max(0, steps.indexOf(currentStatus));

          const isNext = $btn.hasClass("btn-next");

          // Compute new status
          const newIdx = isNext
            ? Math.min(steps.length - 1, idx + 1)
            : Math.max(0, idx - 1);

          const newStatus = steps[newIdx];

          // Tidak ada perubahan status
          if (newStatus === currentStatus) {
            updateTimelineButtons($drawer, steps, newIdx);

            return;
          }

          // =========================
          // Process Update
          // =========================

          const doConfirm = () => {
            // Simpan posisi page saat ini
            let currentPage = 0;

            let table = null;

            if ($.fn.DataTable.isDataTable("#dataTable")) {
              table = $("#dataTable").DataTable();

              currentPage = table.page();
            }

            // Optimistic UI
            setTimelineVisual(newIdx);

            // Disable button
            $drawer.find(".btn-prev, .btn-next").prop("disabled", true);

            // =========================
            // API tidak tersedia
            // =========================

            if (!API_URL || !API_URL.updateStatus) {
              $drawer.data("order-status", newStatus);

              $drawer.find(".btn-prev, .btn-next").prop("disabled", false);

              updateTimelineButtons($drawer, steps, newIdx);

              return;
            }

            // =========================
            // Payload
            // =========================

            const fd = new FormData();

            fd.append("order_id", orderId);

            fd.append("status", newStatus);

            fd.append("device_info", navigator.userAgent || "");

            fd.append("notes", `Changed via drawer to ${newStatus}`);

            // =========================
            // AJAX Update
            // =========================

            $.ajax({
              url: API_URL.updateStatus,

              method: "POST",

              data: fd,

              processData: false,

              contentType: false,

              dataType: "json",

              timeout: 15000,
            })
              .done((res) => {
                if (res && res.success) {
                  // Refresh drawer data
                  fetchAndRender(orderId).done(() => {
                    // Close drawer
                    $drawer.modal("hide");

                    // =========================
                    // Reload WITHOUT reset page
                    // =========================

                    if (table) {
                      table.ajax.reload(function () {
                        // Pastikan tetap di page sebelumnya
                        const pageInfo = table.page.info();

                        const targetPage = Math.min(
                          currentPage,
                          Math.max(pageInfo.pages - 1, 0)
                        );

                        table.page(targetPage).draw("page");
                      }, false);
                    }
                  });
                } else {
                  // =========================
                  // Server failure
                  // =========================

                  setTimelineVisual(idx);

                  $drawer.modal("hide");

                  if (table) {
                    table.ajax.reload(function () {
                      const pageInfo = table.page.info();

                      const targetPage = Math.min(
                        currentPage,
                        Math.max(pageInfo.pages - 1, 0)
                      );

                      table.page(targetPage).draw("page");
                    }, false);
                  }
                }
              })
              .fail((xhr, status, err) => {
                // Revert optimistic UI
                setTimelineVisual(idx);

                alert("Failed to update status on server. Please try again.");

                console.error(
                  "updateStatus error:",
                  err || status,
                  xhr && xhr.responseText
                );
              })
              .always(() => {
                // Enable buttons
                $drawer.find(".btn-prev, .btn-next").prop("disabled", false);

                // Get latest status
                const storedStatus = ($drawer.data("order-status") || "INBOUND")
                  .toString()
                  .toUpperCase();

                const activeIdx = Math.max(0, steps.indexOf(storedStatus));

                updateTimelineButtons($drawer, steps, activeIdx);
              });
          };

          // =========================
          // Confirmation
          // =========================

          if (window.swal && typeof swal === "function") {
            swal({
              title: "Are you sure?",

              text: "This action cannot be undone!",

              type: "warning",

              showCancelButton: true,

              confirmButtonText: "Yes, update it!",

              cancelButtonText: "Cancel",

              confirmButtonClass: "btn btn-success",

              cancelButtonClass: "btn btn-danger m-l-10",

              buttonsStyling: false,
            }).then((confirmed) => {
              if (confirmed) {
                doConfirm();
              }
            });
          } else {
            if (window.confirm(`Set status to "${newStatus}"?`)) {
              doConfirm();
            }
          }

          // =========================
          // Timeline Visual
          // =========================

          function setTimelineVisual(targetIdx) {
            const $items = $drawer.find(".timeline-steps-item");

            $items.each(function (i) {
              const $it = $(this);

              if (i <= targetIdx) {
                $it.addClass("active");
              } else {
                $it.removeClass("active");
              }
            });
          }

          // =========================
          // Timeline Buttons
          // =========================

          function updateTimelineButtons($drawerEl, stepsArr, index) {
            const $prev = $drawerEl.find(".btn-prev");

            const $next = $drawerEl.find(".btn-next");

            $prev.prop("disabled", index <= 0);

            $next.prop("disabled", index >= stepsArr.length - 1);
          }
        }
      );
  };

  // handleDeleteData
  const handleDeleteData = () => {
    $("body").on("click", ".js-delete-data", (e) => {
      const _this = $(e.currentTarget);
      let formData = new FormData();
      const id = _this.attr("data-id");
      console.log(id);

      formData.append("order_id", id);

      const data = {
        url: API_URL.orderDelete,
        method: "POST",
        data: formData,
      };

      Form.deleteData(data);
    });
  };

  // Enhanced status-pill handler: supports toggle-to-clear, optional multi-select, keyboard activation, and aria state sync.
  const handleChangeStatus = () => {
    const $statusSelect = $(".js-change-status");

    $statusSelect.on("change", function () {
      const selectedValue = $(this).val();

      console.log("Status changed to:", selectedValue);
      $(document).trigger("statusFilterChange", [selectedValue]);
    });

    const $searchInput = $("#search");
    let searchTimeout;

    $searchInput.on("keyup", function () {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(() => {
        const searchValue = $(this).val();
        console.log("Searching for:", searchValue);
        $(document).trigger("searchFilterChange", [searchValue]);
      }, 300);
    });

    const filterTable = () => {
      const status =
        $(".js-change-status:visible").val() ||
        $(".js-change-status").first().val();
      const search = $searchInput.val().toLowerCase();

      $("table tbody tr").each(function () {
        const $row = $(this);
        const rowStatus =
          $row.data("status") || $row.find(".status-cell").text();
        const rowText = $row.text().toLowerCase();

        const statusMatch = status === "all" || rowStatus === status;
        const searchMatch = search === "" || rowText.includes(search);

        $row.toggle(statusMatch && searchMatch);
      });
    };

    $statusSelect.on("change", filterTable);
    $searchInput.on("keyup", filterTable);

    const initStatus =
      $(".js-change-status:visible").val() ||
      $(".js-change-status").first().val();
    if (initStatus && initStatus !== "all") {
      console.log("Initial status filter:", initStatus);
    }
  };

  // Flag untuk menandai mode edit
  const isEditMode =
    document.getElementById("order_id") &&
    document.getElementById("order_id").value !== "";

  const handlerPreviewOrderCode = () => {
    // Jika mode edit dan manual order code tidak dicentang, jangan generate ulang
    if (isEditMode && !$("#manualOrderCode").is(":checked")) {
      // Gunakan order_code yang sudah ada
      return;
    }

    // Jika mode add atau manual mode diaktifkan, generate preview
    if (!isEditMode || $("#manualOrderCode").is(":checked")) {
      $.ajax({
        url: API_URL.previewOrderCode,
        method: "POST",
        dataType: "JSON",
        data: {
          depature_date: $("#depature_date").val(),
          total_qty: $(".js-summary-quantity").val(),
          is_duplicate_data: $("#is_duplicate_data").is(":checked") ? 1 : 0,
        },
        success: (response) => {
          if (response.status === "success") {
            // Hanya update jika manual mode diaktifkan
            if ($("#manualOrderCode").is(":checked")) {
              $("#order_code").val(response.data.order_code);
            }
          } else {
            console.log(response.message);
          }
        },
        error: (xhr, status, error) => {
          console.log(error);
        },
      });
    }
  };

  $(document).on(
    "change input",
    "#depature_date, #is_duplicate_data",
    function () {
      // Jangan generate preview di edit mode
      if (!isEditMode && !$("#manualOrderCode").is(":checked")) {
        handlerPreviewOrderCode();
      }
    }
  );

  $(document).on(
    "input change",
    'input[name^="products["][name$="[quantity]"]',
    function () {
      // Jangan generate preview di edit mode
      if (!isEditMode && !$("#manualOrderCode").is(":checked")) {
        handlerPreviewOrderCode();
      }
    }
  );

  $(document).on("change", "#manualOrderCode", function () {
    const isManual = $(this).is(":checked");
    const $orderCodeInput = $("#order_code");

    // Toggle readonly
    $orderCodeInput.prop("readonly", !isManual);

    if (isManual) {
      // Jika manual diaktifkan, kosongkan field atau biarkan user mengisi manual
      if (!$orderCodeInput.val() || $orderCodeInput.val().startsWith("TSI-")) {
      }
      $orderCodeInput.focus();
    } else {
      // Jika manual dimatikan di edit mode, kembalikan ke order code asli
      if (isEditMode) {
        // Ambil order code asli dari value awal
        const originalOrderCode =
          $orderCodeInput.data("original-value") || $orderCodeInput.val();
        $orderCodeInput.val(originalOrderCode);
      } else {
        // Add mode - generate preview
        handlerPreviewOrderCode();
      }
    }
  });

  $(document).ready(function () {
    const $orderCodeInput = $("#order_code");
    const isManual = $("#manualOrderCode").is(":checked");

    // Simpan nilai asli order code untuk edit mode
    if (isEditMode && $orderCodeInput.val()) {
      $orderCodeInput.data("original-value", $orderCodeInput.val());
    }

    // Set readonly state
    $orderCodeInput.prop("readonly", !isManual);

    // Untuk edit mode, disable auto-generation
    if (isEditMode) {
      // Jangan generate ulang, pertahankan nilai asli
      console.log("Edit mode: order_code will not be auto-generated");
    } else {
      // Add mode - generate preview
      handlerPreviewOrderCode();
    }
  });

  $(document).on("change", "#manualDepartureDate", function () {
    const isManual = $(this).is(":checked");
    const $departureDate = $("#depature_date");
    const $batchNumber = $("#batch_number");

    if (isManual) {
      const currentDepartureDate = $departureDate.val();
      const currentBatchNumber = $batchNumber.val();

      swal({
        title: "Warning!",
        text: "Are you sure you want to change Batch Number and Departure Date to manual?",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, Switch to Manual",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-success",
        cancelButtonClass: "btn btn-danger m-l-10",
        buttonsStyling: false,
      }).then(
        function () {
          // Enable manual mode
          $departureDate.prop("readonly", false);
          $batchNumber.prop("readonly", false);
          $departureDate.removeClass("disabled");
          $batchNumber.removeClass("disabled");

          // Re-initialize datepicker if it was destroyed
          if (!$departureDate.hasClass("hasDatepicker")) {
            $departureDate.datepicker({
              format: "dd/mm/yyyy",
              autoclose: true,
              todayHighlight: true,
              orientation: "bottom",
            });
          } else {
            // Enable if exists
            $departureDate.datepicker("enable");
          }
        },
        function (dismiss) {
          if (dismiss === "cancel") {
            $("#manualDepartureDate").prop("checked", false);
            $departureDate.val(currentDepartureDate);
            $batchNumber.val(currentBatchNumber);
            $departureDate.prop("readonly", true);
            $batchNumber.prop("readonly", true);
            $departureDate.addClass("disabled");
            $batchNumber.addClass("disabled");

            // Destroy datepicker to prevent showing
            if ($departureDate.hasClass("hasDatepicker")) {
              $departureDate.datepicker("destroy");
              $departureDate.removeClass("hasDatepicker");
            }
          }
        }
      );
    } else {
      // Switching back to auto mode
      $departureDate.prop("readonly", true);
      $batchNumber.prop("readonly", true);
      $departureDate.addClass("disabled");
      $batchNumber.addClass("disabled");

      // Destroy datepicker to prevent showing
      if ($departureDate.hasClass("hasDatepicker")) {
        $departureDate.datepicker("destroy");
        $departureDate.removeClass("hasDatepicker");
      }
    }
  });

  // On page load - destroy datepicker if not manual
  $(document).ready(function () {
    const $departureDate = $("#depature_date");
    const isManual = $("#manualDepartureDate").is(":checked");

    if (!isManual) {
      $departureDate.prop("readonly", true);
      $departureDate.addClass("disabled");

      // Destroy datepicker on load
      if ($departureDate.hasClass("hasDatepicker")) {
        $departureDate.datepicker("destroy");
        $departureDate.removeClass("hasDatepicker");
      }
    }
  });

  const handleEditBatchData = () => {
    $("body").on("click", ".js-edit-all-data", (e) => {
      e.preventDefault();

      const ids = $(".js-select-data:checked")
        .map((_, el) => $(el).val())
        .get();

      if (ids.length === 0) {
        swal(
          "No rows selected",
          "Please select at least one order to edit.",
          "warning"
        );
        return;
      }

      const $modal = $("#modalEditOrder");
      const $form = $modal.find(".js-form-order-edit");

      if ($form.length && $form[0]) {
        $form[0].reset();
      }

      let $selectedIdsWrap = $form.find(".js-selected-ids");
      if (!$selectedIdsWrap.length) {
        $selectedIdsWrap = $('<div class="js-selected-ids"></div>');
        $form.prepend($selectedIdsWrap);
      }
      $selectedIdsWrap.html("");

      ids.forEach((id) => {
        $selectedIdsWrap.append(
          `<input type="hidden" name="ids[]" value="${id}">`
        );
      });

      let $editType = $form.find("#edit_type");
      if (!$editType.length) {
        $editType = $(
          '<input type="hidden" id="edit_type" name="edit_type" value="batch_number">'
        );
        $form.prepend($editType);
      } else {
        $editType.val("batch_number");
      }

      $modal.find('a[data-toggle="tab"]').removeClass("active");
      $modal.find(".tab-pane").removeClass("show active");

      $modal.find('a[href="#tab-batch-number"]').addClass("active");
      $modal.find("#tab-batch-number").addClass("show active");

      $modal.modal("show");
    });

    $("body").on(
      "shown.bs.tab",
      '#modalEditOrder a[data-toggle="tab"]',
      function () {
        const target = $(this).attr("href");
        let editType = "batch_number";

        if (target === "#tab-departure-date") {
          editType = "departure_date";
        } else if (target === "#tab-cancel-outbond") {
          editType = "cancel_outbond";
        } else if (target === "#tab-inbound-rebook") {
          editType = "inbound_rebook";
        }

        $("#edit_type").val(editType);

        this.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest",
        });
      }
    );
  };

  const handleSubmitEditBatchData = () => {
    $("body").on("submit", ".js-form-order-edit", async function (e) {
      e.preventDefault();

      const form = this;
      const $form = $(form);
      const $btn = $form.find(".js-button-loader");
      const fd = new FormData();

      const ids = $form
        .find('[name="ids[]"]')
        .map((_, el) => $(el).val())
        .get();

      if (!ids.length) {
        swal("Warning!", "No selected orders found.", "warning");
        return;
      }

      ids.forEach((id) => fd.append("ids[]", id));

      const editType = $form.find('[name="edit_type"]').val() || "batch_number";
      fd.append("edit_type", editType);

      if (editType === "batch_number") {
        const batchNumber = $.trim(
          $form.find('[name="batch_number"]').val() || ""
        );

        if (!batchNumber) {
          swal("Warning!", "Batch number is required.", "warning");
          return;
        }

        fd.append("batch_number", batchNumber);
      }

      if (editType === "departure_date") {
        const departureDate = $.trim(
          $form.find('[name="departure_date"]').val() || ""
        );

        if (!departureDate) {
          swal("Warning!", "Departure date is required.", "warning");
          return;
        }

        fd.append("departure_date", departureDate);
      }

      if (editType === "cancel_outbond") {
        const cancelOutbondConfirm = $form
          .find('[name="cancel_outbond_confirm"]')
          .is(":checked")
          ? "1"
          : "0";

        if (cancelOutbondConfirm !== "1") {
          swal("Warning!", "Please confirm cancel outbound first.", "warning");
          return;
        }

        fd.append("cancel_outbond_confirm", cancelOutbondConfirm);
      }

      if (editType === "inbound_rebook") {
        const rebookToInbound = $form
          .find('[name="rebook_to_inbound"]')
          .is(":checked")
          ? "1"
          : "0";
        const rebookBatchNumber = $.trim(
          $form.find('[name="rebook_batch_number"]').val() || ""
        );
        const rebookDepartureDate = $.trim(
          $form.find('[name="rebook_departure_date"]').val() || ""
        );

        if (!rebookBatchNumber) {
          swal("Warning!", "Rebook batch number is required.", "warning");
          return;
        }

        if (!rebookDepartureDate) {
          swal("Warning!", "Rebook departure date is required.", "warning");
          return;
        }

        fd.append("rebook_to_inbound", rebookToInbound);
        fd.append("rebook_batch_number", rebookBatchNumber);
        fd.append("rebook_departure_date", rebookDepartureDate);
      }

      $btn.prop("disabled", true).addClass("is-loading");

      try {
        const result = await HttpRequest.custom({
          url: API_URL.orderEditMultiple,
          method: "POST",
          data: fd,
          isFormData: true,
        });

        if (result.code !== 200) {
          swal({
            title: "Error!",
            text: result.message || "Failed to update selected orders.",
            type: "error",
            confirmButtonClass: "btn btn-custom",
            timer: 3000,
          });
        } else {
          swal({
            title: "Success!",
            text: result.message || "Selected orders updated successfully.",
            type: "success",
            confirmButtonClass: "btn btn-custom",
            timer: 3000,
          });

          $("#modalEditOrder").modal("hide");

          if ($.fn.DataTable.isDataTable("#dataTable")) {
            $("#dataTable").DataTable().ajax.reload(null, false);
          }

          $(".js-select-all-data").prop("checked", false);
          $(".js-select-data").prop("checked", false);
          $(".js-select-all").prop("checked", false);
        }
      } catch (error) {
        console.error(error);
        swal({
          title: "Error!",
          text: "Something went wrong.",
          type: "error",
          confirmButtonClass: "btn btn-custom",
          timer: 3000,
        });
      } finally {
        $btn.prop("disabled", false).removeClass("is-loading");
      }
    });
  };

  /* =========================
    CANCEL ORDER
  ========================= */
  const handleCancelData = () => {
    $("body").on("click", ".js-cancel-data", function (e) {
      e.preventDefault();
      e.stopPropagation();

      // Check if the clicked element has is-clickable class
      const $this = $(this);
      if (!$this.hasClass("is-clickable")) {
        // Optional: Show message or just ignore
        console.log("Cancel is only available for OUTBOUND orders");
        return;
      }

      const id = $this.data("id");
      if (!id) return;

      // Get order code for better user feedback
      const $row = $this.closest("tr");
      const orderCode = $row.find(".order-code").text().trim() || "this order";

      swal({
        title: "Are you sure?",
        text: `Order ${orderCode} outbound shipment will be cancelled. This action cannot be undone!`,
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it!",
        cancelButtonText: "No, keep it",
        confirmButtonClass: "btn btn-success",
        cancelButtonClass: "btn btn-danger m-l-10",
        buttonsStyling: false,
      }).then(async function () {
        try {
          let formData = new FormData();
          formData.append("ids[]", id);
          formData.append("edit_type", "cancel_outbond");
          formData.append("cancel_outbond_confirm", "1");

          const response = await HttpRequest.custom({
            url: API_URL.orderEditMultiple,
            method: "POST",
            data: formData,
            isFormData: true,
          });

          if (response.code === 200) {
            swal(
              "Cancelled!",
              response.message || "Order cancelled successfully.",
              "success"
            );

            // Reload DataTable
            if ($.fn.DataTable.isDataTable("#dataTable")) {
              $("#dataTable").DataTable().ajax.reload(null, false);
            }
          } else {
            swal(
              "Failed!",
              response.message || "Failed to cancel order.",
              "error"
            );
          }
        } catch (error) {
          console.error(error);
          swal("Error!", "Something went wrong. Please try again.", "error");
        }
      });
    });
  };

  $(document).on(
    "change input",
    "#depature_date, #is_duplicate_data",
    function () {
      // Jangan generate preview di edit mode
      if (!isEditMode && !$("#manualOrderCode").is(":checked")) {
        handlerPreviewOrderCode();
      }
    }
  );

  // init
  const init = () => {
    if ($(".js-data-order").length) {
      handleRunDataTable();
      handleDeleteData();
      handleChangeStatus();
      handlePrintData();
      handlePrintAllData();
      handleEditBatchData();
      handleSubmitEditBatchData();
      handleCancelData();
    }
    if ($("#orderDetailDrawer").length) {
      handleGetDetailData && handleGetDetailData();
    }
    if ($(".js-form-order").length) {
      handleRunValidation();
      handleClickValidation();
      // handleEmptyInput();
      handleOpenCamera(); // desktop

      renumberAllProductRows();
      handleAddProductRow();
      updateProductSummary();

      // initialize server photos into capturedPhotos and render them
      initServerPhotos();
      // register scan handlers if needed
    }
  };

  return {
    init,
  };
})();

export default Order;
